import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  AppBar,
  Toolbar,
  Avatar,
  Container,
  Alert,
  Snackbar,
  Tooltip,
  Breadcrumbs,
  Link,
  Autocomplete,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
} from "@mui/material";
import {
  Upload as UploadIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Check as CheckIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import axios from "axios";
import LaboratoryService from "../../services/laboratoryService";
import { getCountries, getStates, getCities } from "../../services/api";
import FileUploadComponent from "../../components/FileUploadComponent";
import {decryptText} from "../../utils/encryption";
import S3Utils from "../../utils/s3Utils";

const AssessorEditById = () => {
   const location = useLocation();
    const getOrgIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedOrgId = searchParams.get("orgId");
      if (encryptedOrgId) {
        try {
          const decryptedOrgId = decryptText(encryptedOrgId);
          return decryptedOrgId;
        } catch (error) {
          return encryptedOrgId;
        }
      }
      return null;
    };
    const getOrgId = () => {
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      return queryOrgId;
    }
    return null;
  };
  const [assessorOrgId, setAssessorOrgId] = useState(getOrgId());
  const [activeTab, setActiveTab] = useState("basicdetail");
  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);
  // dynamic lists for cascading selects
  const [countries, setCountries] = useState([
    { id: 101, name: "India", iso: "IN", phonecode: "91" },
  ]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isOrgIdExist, setIsOrgIdExist] = useState(false);
  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  const [loadingDisciplines, setLoadingDisciplines] = useState(false);
  const [assessorStatus, setAssessorStatus] = useState(null);
  const [isFormEditable, setIsFormEditable] = useState(true);
  const [verifyingLabId, setVerifyingLabId] = useState(false);
  const [labVerified, setLabVerified] = useState(false);
  const [verifiedLabData, setVerifiedLabData] = useState(null);

  // Map accreditation type to labTypeId
  const labTypeMapping = {
    Testing: 1,
    Calibration: 2,
    Medical: 3,
  };

  // Map field of expertise value to label
  const fieldOfExpertiseLabels = {
    1: "Testing",
    2: "Calibration",
    3: "Medical",
  };

  const bindSalutation = {
    "1": "Dr.",
    "2": "Mr.",
    "3": "Ms.",
    "4": "Mrs."
  };


  // Load Qualifications from API
  const loadQualifications = async (orgId) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/get-qualifications/${orgId}?user=6`;
      const resp = await axios.get(url);

      if (resp.data.success && Array.isArray(resp.data.data)) {
        const formattedData = resp.data.data.map((data) => ({
          id: data.id,
          degree: data.Degree,
          college: data.College,
          specialization: data.Specialization,
          yearOfPassing: data.YearOfPassing,
          country: data.Country,
          countryName: data.countryname,
          state: data.State,
          stateName: data.Statename,
          city: data.City,
          cityName: data.Cityname,
          document: createFileObject(data.UploadDocument),
          visible: data.visible, // Track visibility status
        }));

        setQualifications(formattedData);
      }
    } catch (error) {
      console.error("Error loading qualifications:", error);
    }
  };

  // Load Work Experiences from API
  const loadWorkExperiences = async (orgId) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/get-work-experiences/${orgId}?user=6`;
      const resp = await axios.get(url);
      if (resp.data.success && resp.data.data) {
        // Map API response to frontend format
        const mappedExperiences = resp.data.data.map((exp) => {
          // Parse Period field (format: "MM/DD/YYYY~MM/DD/YYYY" or "MM/DD/YYYY")
          const periodParts = exp.Period ? exp.Period.split("~") : [];
          const startDate = periodParts[0]
            ? new Date(periodParts[0]).toISOString().split("T")[0]
            : "";
          const endDate = periodParts[1]
            ? new Date(periodParts[1]).toISOString().split("T")[0]
            : "";
          const currentlyWorking = periodParts.length === 1; // Only start date means currently working

          // Parse Experience12 field (format: "X Year : Y Month : Z Days")
          const expParts = exp.Experience12 ? exp.Experience12.split(":") : [];
          const years = expParts[0]
            ? parseInt(expParts[0].trim().split(" ")[0]) || 0
            : 0;
          const months = expParts[1]
            ? parseInt(expParts[1].trim().split(" ")[0]) || 0
            : 0;
          const days = expParts[2]
            ? parseInt(expParts[2].trim().split(" ")[0]) || 0
            : 0;

          return {
            id: exp.id,
            employer: exp.Employer || "",
            country: exp.Country || "101",
            countryName: exp.countryname || "",
            state: exp.State || "",
            stateName: exp.Statename || "",
            city: exp.City || exp.city || "",
            cityName: exp.Cityname || "",
            startDate: startDate,
            endDate: endDate,
            currentlyWorking: currentlyWorking,
            position: exp.Position || "",
            responsibilities: exp.Responsibilities || "",
            accreditationExperience: exp.expDetails || "",
            experienceYears: years,
            experienceMonths: months,
            experienceDays: days,
            document: createFileObject(exp.UploadDocument),
            isNablAccredited: exp.isExp || "",
            relevantExperience: exp.RelevantExp || "0",
            visible: exp.visible, // Track visibility status
          };
        });
        setExperiences(mappedExperiences);
      }
    } catch (error) {
      console.error("Error loading work experiences:", error);
    }
  };

  // Load Trainings from API
  const loadTrainings = async (orgId) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/get-trainings/${orgId}?user=6`;
      const resp = await axios.get(url);
      if (resp.data.success && resp.data.data) {
        const mappedTrainings = resp.data.data.map((training) => {
          // Parse Period field (format: "YYYY-MM-DD~YYYY-MM-DD" or "YYYY-MM-DD")
          const periodParts = training.Period
            ? training.Period.split("~")
            : ["", ""];
          return {
            id: training.id,
            trainingName: training.Nametraining || "",
            organizedBy: training.OrganizedBy || "",
            country: parseInt(training.Country) || 101,
            countryName: training.countryname || "",
            state: parseInt(training.State) || "",
            stateName: training.Statename || "",
            city: parseInt(training.City) || "",
            cityName: training.Cityname || "",
            periodFrom: periodParts[0] || "",
            periodTo: periodParts[1] || "",
            certificate: createFileObject(training.Certificate),
            visible: training.visible, // Track visibility status (can be string "false" or number 0)
          };
        });
        setTrainings(mappedTrainings);
      }
    } catch (error) {
      console.error("Error loading trainings:", error);
    }
  };

  // Helper function to convert file name to file object for FileUploadComponent
  const createFileObject = (fileName) => {
    if (!fileName) return null;
    return {
      fileName: fileName,
      key: `live/${fileName}`, // Just the filename, backend will add live/ prefix
      originalName: fileName,
    };
  };

  // Load Assessor Basic Details from API
  const loadAssessorDetails = async (orgId) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/get-basic-details/${orgId}`;
      const resp = await axios.get(url);
      console.log("Assessor Details Response:", resp.data);
      if (resp.data.success && resp.data.data) {
        const data = resp.data.data;
        if (data.OrgId) {
          setIsOrgIdExist(true);
        }

        // Set assessor status
        setAssessorStatus(data.status);

        // Check if required fields are filled (treat null, undefined, and empty string as not filled)
        const requiredFieldsFilled =
          data.residentialLatitude &&
          data.residentialLatitude !== null &&
          data.residentialLongitude &&
          data.residentialLongitude !== null &&
          data.officeLatitude &&
          data.officeLatitude !== null &&
          data.officeLongitude &&
          data.officeLongitude !== null &&
          data.orgNABLaccridiated !== null &&
          data.empStatus !== null &&
          (data.orgNABLaccridiated === "Yes"
            ? data.FieldType &&
              data.FieldType !== null &&
              data.Discipline &&
              data.Discipline !== null &&
              data.Labid &&
              data.Labid !== null
            : true);

        // Form is editable if:
        // 1. Status is not 1 (normal edit mode), OR
        // 2. Status is 1 AND required fields are NOT filled (can fill GPS/NABL)
        const canEdit =
          data.status !== 1 || (data.status === 1 && !requiredFieldsFilled);
        setIsFormEditable(canEdit);

        // Convert DOB to YYYY-MM-DD format (handles both MM/DD/YYYY and YYYY-MM-DD formats)
        let formattedDob = "";
        if (data.DOB) {
          // Check if it's already in YYYY-MM-DD format
          if (data.DOB.includes("-") && data.DOB.match(/^\d{4}-\d{2}-\d{2}$/)) {
            formattedDob = data.DOB;
          } else if (data.DOB.includes("/")) {
            // Convert MM/DD/YYYY to YYYY-MM-DD
            const parts = data.DOB.split("/");
            if (parts.length === 3) {
              formattedDob = `${parts[2]}-${parts[0].padStart(
                2,
                "0"
              )}-${parts[1].padStart(2, "0")}`;
            }
          }
        }

        setFormData({
          salutation: data.Salutation ? String(data.Salutation) : "",
          name: data.Name || "",
          dob: formattedDob,
          panCardNo: data.PANCard || "",
          aadharCardNo: data.AadharCard || "",
          currentlyWorking: data.empStatus == 1 ? "Yes" : "No",
          officeCountry: data.OfficeCountry || 101,
          officeState: data.OfficeState || "",
          officeCity: data.OfficeCity || "",
          officeDistrict: data.OfficeDistrict || "",
          officePinCode: data.OfficePinCode || "",
          officeMobileNo: data.OfficeMobile || "",
          officeTelephoneNo: data.OfficeTele || "",
          officeEmail: data.OfficeEmail || "",
          officeLatitude: data.officeLatitude || "",
          officeLongitude: data.officeLongitude || "",
          residentialCountry: data.ResidentCountry || 101,
          residentialState: data.ResidenceState || "",
          residentialCity: data.ResidenceCity || "",
          residentialDistrict: data.ResidenceDistrict || "",
          residentialPinCode: data.ResidencePinCode || "",
          residentialMobileNo: data.ResidenceMobile || "",
          residentialTelephoneNo: data.ResidenceTele || "",
          residentialEmail: data.ResidenceEmail || "",
          residentialLatitude: data.residentialLatitude || "",
          residentialLongitude: data.residentialLongitude || "",
          correspondenceAddress: data.CorrespondAddress || "",
          currentEmployer: data.CurrentEmployer || "",
          currentPosition: data.CurrentPosition || "",
          panCardFile: createFileObject(data.UploadPanFile),
          bankProofFile: createFileObject(data.UploadCencelCheque),
          photoFile: createFileObject(data.Fileupload),
          isNablAccredited: data.orgNABLaccridiated,
          nablAccreditationType: data.FieldType,
          nablDisciplines: [], // Will be set after auto-verifying LAB-ID
          labId: data.Labid || "",
          labname: data.labname || "",
          capacity: data.AssessorCapacity || "",
          exemptFromAllotment: data.exemptFromAllotment || "",
        });

        // Load cities for office and residential states
        if (data.OfficeState) {
          await loadCities(data.OfficeState);
        }
        if (data.ResidenceState && data.ResidenceState !== data.OfficeState) {
          await loadCities(data.ResidenceState);
        }
        
        // Auto-verify LAB-ID if present to load discipline names
        if (data.Labid && data.FieldType) {
          setLabVerified(true);
          try {
            const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Checklabid?labid=${data.Labid}`;
            const response = await axios.get(apiUrl);
            
            if (response.data && response.data.isstatus && response.data.lab && Array.isArray(response.data.lab) && response.data.lab.length > 0) {
              const labData = response.data.lab[0];
              setVerifiedLabData(labData);
              
              // Parse disciplines from comma-separated string
              const disciplineNames = labData.disciplines ? labData.disciplines.split(',').map(d => d.trim()) : [];
              const disciplinesArray = disciplineNames.map((name, index) => ({
                id: `${index + 1}`,
                disciplineName: name,
              }));
              
              setAvailableDisciplines(disciplinesArray);
              
              // Map saved discipline IDs from database to the verified discipline IDs
              const savedDisciplineIds = data.Discipline ? (typeof data.Discipline === 'string' ? data.Discipline.split(',').map(id => id.trim()) : data.Discipline) : [];
              
              // Update formData with saved disciplines after availableDisciplines is set
              setFormData(prevData => ({
                ...prevData,
                nablDisciplines: savedDisciplineIds,
              }));
              
              console.log("✅ Auto-verified LAB-ID and loaded disciplines:", disciplinesArray);
              console.log("✅ Mapped saved disciplines:", savedDisciplineIds);
            }
          } catch (error) {
            console.error("Error auto-verifying LAB-ID:", error);
          }
        }
      }
    } catch (error) {
      console.error("Error loading assessor details:", error);
    }
  };

  // Load Agreement status from API
  const loadAgreementStatus = async (orgId) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/get-agreement/${orgId}`;
      const resp = await axios.get(url);
      if (resp.data && resp.data.success && resp.data.data) {
        const data = resp.data.data;

        // Set agreement acceptance status
        setAgreementAccepted(data.TermCondition_I_accept === 1);

        // Set agreement date
        if (data.TermCondition_accept_date) {
          setAgreementDate(data.TermCondition_accept_date);
        }

        // Map witness data
        const witnessData = [];
        if (data.WitnessName1) {
          witnessData.push({
            id: 1,
            name: data.WitnessName1 || "",
            address: data.WitnessAddress1 || "",
            mobile: data.WitnessMobile1 || "",
            email: data.WitnessEmail1 || "",
          });
        }
        if (data.WitnessName2) {
          witnessData.push({
            id: 2,
            name: data.WitnessName2 || "",
            address: data.WitnessAddress2 || "",
            mobile: data.WitnessMobile2 || "",
            email: data.WitnessEmail2 || "",
          });
        }

        if (witnessData.length > 0) {
          setWitnesses(witnessData);
        }
      }
    } catch (error) {
      console.error("Error loading agreement status:", error);
    }
  };

  // Load Referees from API
  const loadReferees = async (orgId) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/get-referee/${orgId}`;
      const resp = await axios.get(url);
      if (resp.data && resp.data.success && resp.data.data) {
        const data = resp.data.data;
        // Map single referee object to array format
        const mappedReferee = {
          id: data.Orgid || orgId,
          referenceType: data.Authority || "",
          name: data.Name || "",
          designation: data.Designation || "",
          organization: data.Organization || "",
          country: data.Country || 101,
          countryName: data.countryname || "",
          state: data.State || "",
          stateName: data.Statename || "",
          city: data.City || "",
          cityName: data.Cityname || "",
          mobile: data.Mobile || "",
          email: data.Email || "",
        };
        setReferees([mappedReferee]);
      }
    } catch (error) {
      console.error("Error loading referees:", error);
    }
  };

  useEffect(() => {
    //loadCountry();
    loadStates("101");
    loadAssessorDetails(assessorOrgId);
    loadQualifications(assessorOrgId);
    loadWorkExperiences(assessorOrgId);
    loadTrainings(assessorOrgId);
    loadExpertiseFields(assessorOrgId);
    loadReferees(assessorOrgId);
    loadAgreementStatus(assessorOrgId);
  }, []);

  const loadStates = async () => {
    try {
      const response = await getStates("101");
      const statesData = response.data?.data || response.data || [];
      setStates(Array.isArray(statesData) ? statesData : []);
    } catch (error) {
      console.error("Error loading states:", error);
      setStates([]);
    }
  };

  const loadCities = async (stateId) => {
    try {
      const response = await getCities(stateId);
      const citiesData = response.data?.data || response.data || [];
      setCities(Array.isArray(citiesData) ? citiesData : []);
    } catch (error) {
      console.error("Error loading cities:", error);
      setCities([]);
    }
  };

  // Load Disciplines based on accreditation type
  const loadDisciplines = async (accreditationType) => {
    if (!accreditationType) {
      setAvailableDisciplines([]);
      return;
    }

    const labTypeId = labTypeMapping[accreditationType];
    if (!labTypeId) {
      setAvailableDisciplines([]);
      return;
    }

    setLoadingDisciplines(true);
    try {
      const appId = "";
      const orgId = "";
      let disciplineType = "Diagnostic";
      const result = await LaboratoryService.getDisciplines(
        labTypeId,
        disciplineType,
        orgId,
        appId
      );
      console.log("Disciplines Load Result:", result);

      if (result.success && result.data) {
        const disciplinesToDisplay = Array.isArray(result.data)
          ? result.data
          : [];
        setAvailableDisciplines(disciplinesToDisplay);
      } else {
        setAvailableDisciplines([]);
        showSnackbar("Failed to load disciplines", "error");
      }
    } catch (error) {
      console.error("Error loading disciplines:", error);
      setAvailableDisciplines([]);
      showSnackbar("Error loading disciplines", "error");
    } finally {
      setLoadingDisciplines(false);
    }
  };

  // Handlers for cascading selects
  const handleOfficeCountryChange = async (event) => {
    const countryId = event.target.value;
    setFormData({
      ...formData,
      officeCountry: countryId,
      officeState: "",
      officeCity: "",
    });
    await loadStates(countryId);
    setCities([]);
  };

  const handleOfficeStateChange = async (event) => {
    const stateId = event.target.value;
    setFormData({ ...formData, officeState: stateId, officeCity: "" });
    await loadCities(stateId);
  };

  const handleResidentialCountryChange = async (event) => {
    const countryId = event.target.value;
    setFormData({
      ...formData,
      residentialCountry: countryId,
      residentialState: "",
      residentialCity: "",
    });
    await loadStates(countryId);
    setCities([]);
  };

  const handleResidentialStateChange = async (event) => {
    const stateId = event.target.value;
    setFormData({
      ...formData,
      residentialState: stateId,
      residentialCity: "",
    });
    await loadCities(stateId);
  };

  // References (Referee) cascading handlers
  const handleRefereeCountryChange = async (event) => {
    const countryId = event.target.value;
    setRefereeForm({
      ...refereeForm,
      country: countryId,
      state: "",
      city: "",
    });
    await loadStates(countryId);
    setCities([]);
  };

  const handleRefereeStateChange = async (event) => {
    const stateId = event.target.value;
    setRefereeForm({ ...refereeForm, state: stateId, city: "" });
    await loadCities(stateId);
  };

  // For qualification and experience forms (local form state)
  const handleQualificationCountryChange = async (event) => {
    const countryId = event.target.value;
    setQualificationForm({
      ...qualificationForm,
      country: countryId,
      state: "",
      city: "",
    });
    await loadStates(countryId);
    setCities([]);
  };

  const handleQualificationStateChange = async (event) => {
    const stateId = event.target.value;
    setQualificationForm({ ...qualificationForm, state: stateId, city: "" });
    await loadCities(stateId);
  };

  const handleExperienceCountryChange = async (event) => {
    const countryId = event.target.value;
    setExperienceForm({
      ...experienceForm,
      country: countryId,
      state: "",
      city: "",
    });
    await loadStates(countryId);
    setCities([]);
  };

  const handleExperienceStateChange = async (event) => {
    const stateId = event.target.value;
    setExperienceForm({ ...experienceForm, state: stateId, city: "" });
    await loadCities(stateId);
  };

  const steps = [
    "Basic Details",
    "Qualifications",
    "Work Experience",
    "Professional Training",
    "Field Of Expertise",
    "References",
    "Agreement",
  ];

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: "", // 'qualification', 'experience', 'training'
    id: null,
    title: "",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Enhanced Basic Details State
  const [formData, setFormData] = useState({
    salutation: "",
    name: "",
    dob: "",
    capacity: "",
    panCardNo: "",
    aadharCardNo: "",
    currentlyWorking: "Yes",
    currentEmployer: "",
    currentPosition: "",
    isNablAccredited: "",
    nablAccreditationType: "",
    nablDisciplines: [],
    officeCountry: 101,
    officeState: "",
    officeCity: "",
    officeDistrict: "",
    officePinCode: "",
    officeMobileNo: "",
    officeTelephoneNo: "",
    officeEmail: "",
    officeLatitude: "",
    officeLongitude: "",
    residentialCountry: 101,
    residentialState: "",
    residentialCity: "",
    residentialDistrict: "",
    residentialPinCode: "",
    residentialMobileNo: "",
    residentialTelephoneNo: "",
    residentialEmail: "",
    residentialLatitude: "",
    residentialLongitude: "",
    correspondenceAddress: "Residence",
    panCardFile: "",
    cancelCheckFile: "",
    photoFile: "",
    labId: "",
    labname: "",
    exemptFromAllotment: "",
  });

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  // Get current location for Office Address
  const getOfficeCurrentLocation = () => {
    if (!navigator.geolocation) {
      showSnackbar("Geolocation is not supported by this browser", "error");
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lng = position.coords.longitude.toFixed(6);

        setFormData({
          ...formData,
          officeLatitude: lat,
          officeLongitude: lng,
        });

        const accuracy = position.coords.accuracy;
        const accuracyText =
          accuracy < 100
            ? "high accuracy"
            : accuracy < 500
            ? "medium accuracy"
            : "low accuracy";
        showSnackbar(
          `Office location detected (${accuracyText}: ±${Math.round(
            accuracy
          )}m)`,
          "success"
        );
        setLoading(false);
      },
      (error) => {
        let errorMessage = "Failed to get current location";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location access denied. Please allow location access.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage =
              "Location unavailable. Please try again or enter manually.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            break;
        }
        showSnackbar(errorMessage, "error");
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  // Get current location for Residential Address
  const getResidentialCurrentLocation = () => {
    if (!navigator.geolocation) {
      showSnackbar("Geolocation is not supported by this browser", "error");
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lng = position.coords.longitude.toFixed(6);

        setFormData({
          ...formData,
          residentialLatitude: lat,
          residentialLongitude: lng,
        });

        const accuracy = position.coords.accuracy;
        const accuracyText =
          accuracy < 100
            ? "high accuracy"
            : accuracy < 500
            ? "medium accuracy"
            : "low accuracy";
        showSnackbar(
          `Residential location detected (${accuracyText}: ±${Math.round(
            accuracy
          )}m)`,
          "success"
        );
        setLoading(false);
      },
      (error) => {
        let errorMessage = "Failed to get current location";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location access denied. Please allow location access.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage =
              "Location unavailable. Please try again or enter manually.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            break;
        }
        showSnackbar(errorMessage, "error");
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  const handleFileUpload = (setFileFunction) => (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileFunction(file);
      showSnackbar("File uploaded successfully!", "success");
    }
  };

  const handleRemoveFile = (setFileFunction) => () => {
    setFileFunction(null);
    showSnackbar("File removed", "info");
  };

  // Verify LAB-ID from NABL API
  const verifyLabId = async () => {
    if (!formData.labId || !formData.nablAccreditationType) {
      showSnackbar("Please enter LAB-ID and select Accreditation Type first", "error");
      return;
    }

    setVerifyingLabId(true);
    setLabVerified(false);
    setVerifiedLabData(null);

    try {
      const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Checklabid?labid=${formData.labId}`;
      console.log("Verifying LAB-ID:", apiUrl);

      const response = await axios.get(apiUrl);
      console.log("LAB-ID Verification Response:", response.data);

      // Handle new response structure: { isstatus: true, message: "...", lab: [{...}] }
      if (response.data && response.data.isstatus && response.data.lab && Array.isArray(response.data.lab) && response.data.lab.length > 0) {
        const labData = response.data.lab[0];
        setVerifiedLabData(labData);
        setLabVerified(true);

        // Parse disciplines from comma-separated string (field name is "disciplines")
        const disciplineNames = labData.disciplines ? labData.disciplines.split(',').map(d => d.trim()) : [];
        const disciplinesArray = disciplineNames.map((name, index) => ({
          id: `${index + 1}`,
          disciplineName: name,
        }));

        setAvailableDisciplines(disciplinesArray);
        
        // Auto-select all disciplines and update formData
        const allDisciplineIds = disciplinesArray.map(d => d.id);
        setFormData(prevData => ({
          ...prevData,
          labname: labData.name || "",
          nablDisciplines: allDisciplineIds, // Auto-bind all disciplines
        }));
        
        showSnackbar(`LAB-ID verified successfully! Lab: ${labData.name}`, "success");
      } else {
        showSnackbar("LAB-ID not found or invalid", "error");
        setAvailableDisciplines([]);
      }
    } catch (error) {
      console.error("Error verifying LAB-ID:", error);
      showSnackbar("Failed to verify LAB-ID. Please check the LAB-ID and try again.", "error");
      setAvailableDisciplines([]);
    } finally {
      setVerifyingLabId(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      // Validate mandatory fields
      const mandatoryFields = [
        { field: formData.name, name: "Name" },
        { field: formData.capacity, name: "Capacity" },
        { field: formData.dob, name: "Date of Birth" },
        { field: formData.panCardNo, name: "PAN Card Number" },
        { field: formData.aadharCardNo, name: "Aadhar Card Number" },
        { field: formData.currentlyWorking, name: "Employment Status" },
        { field: formData.currentEmployer, name: "Name of Current Employer" },
        { field: formData.currentPosition, name: "Current Position" },
        { field: formData.residentialCountry, name: "Residential Country" },
        { field: formData.residentialState, name: "Residential State" },
        { field: formData.residentialCity, name: "Residential City" },
        { field: formData.residentialPinCode, name: "Residential Pin Code" },
        {
          field: formData.residentialMobileNo,
          name: "Residential Mobile Number",
        },
        { field: formData.residentialEmail, name: "Residential Email" },
        {
          field: formData.correspondenceAddress,
          name: "Correspondence Address",
        },
        { field: formData.panCardFile, name: "PAN Card Upload" },
        { field: formData.bankProofFile, name: "Bank Proof Upload" },
        { field: formData.photoFile, name: "Photograph Upload" },
        { field: formData.officeLatitude, name: "Office Latitude" },
        { field: formData.officeLongitude, name: "Office Longitude" },
        { field: formData.residentialLatitude, name: "Residential Latitude" },
        { field: formData.residentialLongitude, name: "Residential Longitude" },
        { field: formData.isNablAccredited, name: "NABL Accredited" },
        ...(formData.isNablAccredited === "Yes"
          ? [
              {
                field: formData.nablAccreditationType,
                name: "NABL Accreditation Type",
              },
              { field: formData.nablDisciplines, name: "NABL Disciplines" },
              { field: formData.labId, name: "Lab ID" },
            ]
          : []),
      ];
      console.log("Mandatory Fields:", mandatoryFields);
      const missingFields = mandatoryFields
        .filter(({ field }) => !field || field === "")
        .map(({ name }) => name);

      if (missingFields.length > 0) {
        showSnackbar(
          `Please fill all mandatory fields: ${missingFields.join(", ")}`,
          "error"
        );
        setLoading(false);
        return;
      }

      const payload = {
        OrgId: assessorOrgId,
        Salutation: formData.salutation,
        name: formData.name,
        dob: formData.dob,
        empStatus: formData.currentlyWorking === "Yes" ? "1" : "0",
        CurrentEmployer: formData.currentEmployer,
        CurrentPosition: formData.currentPosition,
        OfficeCountry: formData.officeCountry,
        OfficeState: formData.officeState,
        OfficeDistrict: formData.officeDistrict,
        OfficeCity: formData.officeCity,
        OfficePinCode: formData.officePinCode,
        officeMobile: formData.officeMobileNo,
        OfficeTele: formData.officeTelephoneNo,
        OfficeEmail: formData.officeEmail,
        officeLatitude: formData.officeLatitude,
        officeLongitude: formData.officeLongitude,
        ResidentCountry: formData.residentialCountry,
        ResidenceState: formData.residentialState,
        ResidenceDistrict: formData.residentialDistrict,
        ResidenceCity: formData.residentialCity,
        ResidencePinCode: formData.residentialPinCode,
        ResidenceMobile: formData.residentialMobileNo,
        residenceTele: formData.residentialTelephoneNo,
        ResidenceEmail: formData.residentialEmail,
        residentialLatitude: formData.residentialLatitude,
        residentialLongitude: formData.residentialLongitude,
        CorrespondAddress: formData.correspondenceAddress,
        PANCard: formData.panCardNo,
        AadharCard: formData.aadharCardNo,
        isNablAccredited: formData.isNablAccredited,
        nablAccreditationType: formData.nablAccreditationType,
        nablDisciplines: Array.isArray(formData.nablDisciplines)
          ? formData.nablDisciplines.join(",")
          : formData.nablDisciplines,
        PanUploadFile: formData.panCardFile?.fileName || "",
        CancelChequeUploadfile: formData.bankProofFile?.fileName || "",
        Fileupload: formData.photoFile?.fileName || "",
        ClientIP: "0",
        UpdateBy: assessorOrgId,
        capacity: formData.capacity,
        orgNABLaccridiated: formData.isNablAccredited,
        FieldType: formData.nablAccreditationType,
        Discipline: (() => {
          // Map discipline IDs to names for saving
          if (Array.isArray(formData.nablDisciplines)) {
            const disciplineNames = formData.nablDisciplines.map(id => {
              const discipline = availableDisciplines.find(d => String(d.id) === String(id));
              return discipline ? discipline.disciplineName : id;
            });
            return disciplineNames.join(',');
          }
          return formData.nablDisciplines;
        })(),
        Labid: formData.labId,
        labname: formData.labname,
        exemptFromAllotment: formData.exemptFromAllotment,
      };

      // Use update endpoint with PUT method (assuming data already exists for orgId)
      let resp = null;
      if (isOrgIdExist) {
        const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/update-basic-details`;
        resp = await axios.put(url, payload);
      } else {
        const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/insert-basic-details`;
        resp = await axios.post(url, payload);
      }
      if (resp.data.success) {
        showSnackbar(
          `Basic details ${isOrgIdExist ? "updated" : "saved"} successfully!`,
          "success"
        );
        console.log("Form data:", resp.data);
      } else {
        showSnackbar("Failed to save basic details", "error");
      }
    } catch (error) {
      console.error("Error saving basic details:", error);
      showSnackbar("Error saving basic details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevStep) => prevStep + 1);
      const nextTab = [
        "basicdetail",
        "qualification",
        "workexperience",
        "professionaltraining",
        "fieldofexpertise",
        "referee",
        "agreement",
      ][activeStep + 1];
      setActiveTab(nextTab);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevStep) => prevStep - 1);
      const prevTab = [
        "basicdetail",
        "qualification",
        "workexperience",
        "professionaltraining",
        "fieldofexpertise",
        "referee",
        "agreement",
      ][activeStep - 1];
      setActiveTab(prevTab);
    }
  };

  // Enhanced File Upload Component
  const FileUploadField = ({
    label,
    file,
    onFileUpload,
    onRemoveFile,
    accept = "*",
    required = false,
  }) => (
    <Box>
      <FormLabel
        component="legend"
        sx={{
          fontWeight: "bold",
          color: "text.primary",
          display: "block",
          mb: 1,
        }}
      >
        {label} {required && "*"}
      </FormLabel>
      <Box
        sx={{
          border: "2px dashed",
          borderColor: file ? "success.main" : "grey.300",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          backgroundColor: file ? "#f0f9f0" : "#fafafa",
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "#f0f7ff",
          },
        }}
      >
        {file ? (
          <Box>
            <CheckIcon sx={{ color: "success.main", fontSize: 40, mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: "medium", mb: 1 }}>
              {file.name}
            </Typography>
            <Button
              color="error"
              variant="outlined"
              size="small"
              onClick={onRemoveFile}
              startIcon={<DeleteIcon />}
            >
              Remove File
            </Button>
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon sx={{ fontSize: 40, color: "grey.400", mb: 1 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Drag & drop or click to upload
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
            >
              Choose File
              <input
                type="file"
                hidden
                onChange={onFileUpload}
                accept={accept}
              />
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Enhanced Basic Details Section
  const renderBasicDetails = () => (
    <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "text.primary" }}
            >
              Assessor Basic Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please fill in your personal and contact information
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: "primary.light",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "primary.contrastText" }}
          >
            Assessor: {formData.name || "-"}
          </Typography>
        </Box>

        {/* Instructions Card */}
        <Card
          variant="outlined"
          sx={{ mb: 3, border: "2px solid", borderColor: "warning.main" }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <InfoIcon sx={{ color: "warning.main", mr: 1, fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "warning.main" }}
              >
                Instructions for Assessor Profile
              </Typography>
            </Box>

            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
                1. Navigate to the "Basic Details" section in your profile and
                ensure all information is thoroughly reviewed and updated.
              </Typography>

              <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
                2. Mark "Yes" or "No" for the question: "Is your current
                organization NABL accredited?"
              </Typography>

              <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
                3. Enter the latitude and longitude for both your office and
                residence.
              </Typography>

              <Box sx={{ pl: 2, mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "medium", color: "text.secondary" }}
                >
                  A.) If they are at different locations, please obtain the
                  correct coordinates for each and paste them into their
                  respective fields.
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
                4. Once all information in the Basic Details section is updated
                and submitted, you will not be able to edit it again.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, fontWeight: "medium" }}>
                5. The laboratories assigned to you for assessment will appear
                under the "Submitted Application" section, where you must choose
                to accept or reject each assigned application.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: "text.primary",
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  pb: 1,
                }}
              >
                Personal Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Title</InputLabel>
                <Select
                  value={formData.salutation || ""}
                  label="Title"
                  onChange={handleInputChange("salutation")}
                >
                  <MenuItem value="">
                    <em>Select Title</em>
                  </MenuItem>
                  <MenuItem value="1">Dr.</MenuItem>
                  <MenuItem value="2">Mr.</MenuItem>
                  <MenuItem value="3">Ms.</MenuItem>
                  <MenuItem value="4">Mrs.</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={handleInputChange("name")}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Capacity</InputLabel>
                <Select
                  value={formData.capacity}
                  label="Capacity"
                  onChange={handleInputChange("capacity")}
                >
                  <MenuItem value="">
                    <em>Select Capacity</em>
                  </MenuItem>
                  <MenuItem value="LA">LA</MenuItem>
                  <MenuItem value="LA_TA">LA_TA</MenuItem>
                  <MenuItem value="TA">TA</MenuItem>
                  <MenuItem value="TE">TE</MenuItem>
                  <MenuItem value="OBS">OBS</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.dob}
                onChange={handleInputChange("dob")}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="PAN Card Number"
                value={formData.panCardNo}
                onChange={handleInputChange("panCardNo")}
                required
                variant="outlined"
                inputProps={{
                  maxLength: 10,
                  style: { textTransform: "uppercase" },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Aadhar Card Number"
                type="number"
                value={formData.aadharCardNo}
                onChange={handleInputChange("aadharCardNo")}
                variant="outlined"
                inputProps={{ maxLength: 12 }}
              />
            </Grid>

            {/* File Uploads */}
            <Grid item xs={12} md={6}>
              <FileUploadComponent
                fieldName="panCardFile"
                label="Upload PAN Card"
                orgId={assessorOrgId}
                section="assessor-documents"
                required={true}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                multiple={false}
                onChange={(fieldName, fileData) => {
                  setFormData((prev) => ({ ...prev, panCardFile: fileData }));
                }}
                initialFile={formData.panCardFile}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FileUploadComponent
                fieldName="bankProofFile"
                label="Upload Cancel Cheque/Bank Proof"
                orgId={assessorOrgId}
                section="assessor-documents"
                required={true}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                multiple={false}
                onChange={(fieldName, fileData) => {
                  setFormData((prev) => ({ ...prev, bankProofFile: fileData }));
                }}
                initialFile={formData.bankProofFile}
              />
            </Grid>

            {/* Employment Status */}
            <Grid item xs={12}>
              <FormLabel
                component="legend"
                sx={{ fontWeight: "bold", color: "text.primary", mb: 2 }}
              >
                Are you Currently Working?*
              </FormLabel>
              <RadioGroup
                row
                value={formData.currentlyWorking}
                onChange={handleInputChange("currentlyWorking")}
                sx={{ gap: 2 }}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label="No"
                />
              </RadioGroup>
            </Grid>
            {formData.currentlyWorking === "Yes" && (
              <>
                {/* Current Employer */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name of Current Employer"
                    value={formData.currentEmployer}
                    onChange={handleInputChange("currentEmployer")}
                    variant="outlined"
                    required
                  />
                </Grid>

                {/* Current Position */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Current Position"
                    value={formData.currentPosition}
                    onChange={handleInputChange("currentPosition")}
                    variant="outlined"
                    required
                  />
                </Grid>
              </>
            )}

            {/* NABL Accreditation Section */}
            <Grid item xs={12}>
              <FormLabel
                component="legend"
                sx={{ fontWeight: "bold", color: "text.primary", mb: 2 }}
              >
                Is your current working organisation NABL accredited?*
              </FormLabel>
              <RadioGroup
                row
                value={formData.isNablAccredited}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    isNablAccredited: value,
                    nablAccreditationType:
                      value === "No" ? "" : formData.nablAccreditationType,
                    nablDisciplines:
                      value === "No" ? [] : formData.nablDisciplines,
                    labId: value === "No" ? "" : formData.labId,
                  });
                }}
                sx={{ gap: 3 }}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label="No"
                />
              </RadioGroup>
            </Grid>

            {/* Show Accreditation Type if Yes */}
            {formData.isNablAccredited === "Yes" && (
              <>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined" required>
                    <InputLabel>Accreditation Type</InputLabel>
                    <Select
                      value={formData.nablAccreditationType}
                      label="Accreditation Type of Organisation"
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({
                          ...formData,
                          nablAccreditationType: value,
                          nablDisciplines: [], // Reset disciplines when type changes
                        });
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Type</em>
                      </MenuItem>
                      <MenuItem value="Testing">Testing</MenuItem>
                      <MenuItem value="Calibration">Calibration</MenuItem>
                      <MenuItem value="Medical">Medical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="LAB-ID"
                    value={formData.labId}
                    onChange={(e) => {
                      setFormData({ ...formData, labId: e.target.value });
                      setLabVerified(false);
                      setVerifiedLabData(null);
                      setAvailableDisciplines([]);
                    }}
                    variant="outlined"
                    placeholder="e.g., T-4337"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={verifyLabId}
                            disabled={
                              verifyingLabId ||
                              !formData.labId ||
                              !formData.nablAccreditationType
                            }
                            sx={{ minWidth: "80px" }}
                          >
                            {verifyingLabId
                              ? "Verifying..."
                              : labVerified
                              ? "Verified"
                              : "Verify"}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {labVerified && verifiedLabData && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {verifiedLabData.name || formData.labname}
                      </Typography>
                    </Alert>
                  )}
                </Grid>

                {/* Show Disciplines if LAB-ID is verified */}
                {formData.nablAccreditationType && labVerified && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>
                        Disciplines accredited in this lab
                      </InputLabel>
                      <Select
                        multiple
                        value={formData.nablDisciplines}
                        label="Disciplines accredited in this lab"
                        disabled={
                          verifyingLabId || availableDisciplines.length === 0
                        }
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            nablDisciplines: e.target.value,
                          });
                        }}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((id) => {
                              const discipline = availableDisciplines.find(
                                (d) => String(d.id) === String(id)
                              );
                              const label = discipline
                                ? discipline.disciplineName
                                : id;
                              return (
                                <Chip key={id} label={label} size="small" />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {verifyingLabId ? (
                          <MenuItem disabled>
                            <em>Verifying LAB-ID...</em>
                          </MenuItem>
                        ) : availableDisciplines.length === 0 ? (
                          <MenuItem disabled>
                            <em>Please verify LAB-ID first</em>
                          </MenuItem>
                        ) : (
                          availableDisciplines.map((discipline) => {
                            const disciplineId = String(discipline.id);
                            const disciplineName = discipline.disciplineName;
                            return (
                              <MenuItem key={disciplineId} value={disciplineId}>
                                <Checkbox
                                  checked={
                                    formData.nablDisciplines.indexOf(
                                      disciplineId
                                    ) > -1
                                  }
                                />
                                {disciplineName}
                              </MenuItem>
                            );
                          })
                        )}
                      </Select>
                    </FormControl>
                    {!labVerified && formData.labId && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Please click "Verify" button to load disciplines for
                        this LAB-ID
                      </Alert>
                    )}
                  </Grid>
                )}
              </>
            )}

            {/* Exemption from Allotment Validation */}
            <Grid item xs={12}>
              <FormLabel
                component="legend"
                sx={{ fontWeight: "bold", color: "text.primary", mb: 2, mt: 2 }}
              >
                Exempt this assessor from allotment validation?
              </FormLabel>
              <RadioGroup
                row
                value={formData.exemptFromAllotment}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    exemptFromAllotment: e.target.value,
                  });
                }}
                sx={{ gap: 3 }}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label="No"
                />
              </RadioGroup>
            </Grid>

            {/* Address Sections */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: "text.primary",
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  pb: 1,
                }}
              >
                Address Information
              </Typography>
            </Grid>

            {/* Office Address */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <WorkIcon sx={{ mr: 1 }} />
                    Office Address
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Country</InputLabel>
                        <Select
                          value={formData.officeCountry}
                          label="Country"
                          onChange={handleOfficeCountryChange}
                        >
                          <MenuItem value="">
                            <em>Select Country</em>
                          </MenuItem>
                          {countries.map((c) => (
                            <MenuItem
                              key={c.id || c.code}
                              value={c.id || c.code}
                            >
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>State</InputLabel>
                        <Select
                          value={formData.officeState}
                          label="State"
                          onChange={handleOfficeStateChange}
                        >
                          <MenuItem value="">
                            <em>Select State</em>
                          </MenuItem>
                          {states.map((s) => (
                            <MenuItem
                              key={s.id || s.code}
                              value={s.id || s.code}
                            >
                              {s.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>City</InputLabel>
                        <Select
                          value={formData.officeCity}
                          label="City"
                          onChange={handleInputChange("officeCity")}
                        >
                          <MenuItem value="">
                            <em>Select City</em>
                          </MenuItem>
                          {cities.map((c) => (
                            <MenuItem
                              key={c.id || c.code}
                              value={c.id || c.name}
                            >
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="District"
                        value={formData.officeDistrict}
                        onChange={handleInputChange("officeDistrict")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Pin Code"
                        type="number"
                        value={formData.officePinCode}
                        onChange={handleInputChange("officePinCode")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Mobile Number"
                        type="number"
                        value={formData.officeMobileNo}
                        onChange={handleInputChange("officeMobileNo")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Telephone No."
                        value={formData.officeTelephoneNo}
                        onChange={handleInputChange("officeTelephoneNo")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.officeEmail}
                        onChange={handleInputChange("officeEmail")}
                        variant="outlined"
                      />
                    </Grid>

                    {/* Office Location Coordinates */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, textAlign: "center" }}>
                        <Button
                          variant="outlined"
                          startIcon={<MyLocationIcon />}
                          onClick={getOfficeCurrentLocation}
                          disabled={loading}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            borderColor: "primary.main",
                            color: "primary.main",
                            "&:hover": {
                              borderColor: "primary.dark",
                              backgroundColor: "rgba(25, 118, 210, 0.1)",
                            },
                          }}
                        >
                          {loading
                            ? "Getting Location..."
                            : "Get Office Current Location"}
                        </Button>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Or enter coordinates manually below
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Latitude*"
                        value={formData.officeLatitude}
                        onChange={handleInputChange("officeLatitude")}
                        variant="outlined"
                        placeholder="e.g., 28.6217426"
                        helperText="Enter latitude in decimal degrees (between -90 and 90)"
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Longitude*"
                        value={formData.officeLongitude}
                        onChange={handleInputChange("officeLongitude")}
                        variant="outlined"
                        placeholder="e.g., 77.0882695"
                        helperText="Enter longitude in decimal degrees (between -180 and 180)"
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          How to find coordinates:
                        </Typography>
                        <Typography variant="body2" component="div">
                          • Open Google Maps → Right-click your office location
                          → Copy coordinates from the popup
                          <br />
                          • Or click "Get Office Current Location" button above
                          <br />• Format: Use dot (.) not comma - Correct:
                          28.6217426, Incorrect: 28,6217426
                        </Typography>
                      </Alert>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Residential Address */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <PersonIcon sx={{ mr: 1 }} />
                    Residential Address*
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Country*</InputLabel>
                        <Select
                          value={formData.residentialCountry}
                          label="Country"
                          onChange={handleResidentialCountryChange}
                        >
                          <MenuItem value="">
                            <em>Select Country</em>
                          </MenuItem>
                          {countries.map((c) => (
                            <MenuItem
                              key={c.id || c.code}
                              value={c.id || c.code}
                            >
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>State*</InputLabel>
                        <Select
                          value={formData.residentialState}
                          label="State"
                          onChange={handleResidentialStateChange}
                        >
                          <MenuItem value="">
                            <em>Select State</em>
                          </MenuItem>
                          {states.map((s) => (
                            <MenuItem
                              key={s.id || s.code}
                              value={s.id || s.code}
                            >
                              {s.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>City*</InputLabel>
                        <Select
                          value={formData.residentialCity}
                          label="City"
                          onChange={handleInputChange("residentialCity")}
                        >
                          <MenuItem value="">
                            <em>Select City</em>
                          </MenuItem>
                          {cities.map((c) => (
                            <MenuItem
                              key={c.id || c.code}
                              value={c.id || c.name}
                            >
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="District"
                        value={formData.residentialDistrict}
                        onChange={handleInputChange("residentialDistrict")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Pin Code"
                        type="number"
                        value={formData.residentialPinCode}
                        onChange={handleInputChange("residentialPinCode")}
                        variant="outlined"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Mobile Number"
                        type="number"
                        value={formData.residentialMobileNo}
                        onChange={handleInputChange("residentialMobileNo")}
                        variant="outlined"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Telephone No."
                        value={formData.residentialTelephoneNo}
                        onChange={handleInputChange("residentialTelephoneNo")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.residentialEmail}
                        onChange={handleInputChange("residentialEmail")}
                        variant="outlined"
                        required
                      />
                    </Grid>

                    {/* Residential Location Coordinates */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, textAlign: "center" }}>
                        <Button
                          variant="outlined"
                          startIcon={<MyLocationIcon />}
                          onClick={getResidentialCurrentLocation}
                          disabled={loading}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            borderColor: "primary.main",
                            color: "primary.main",
                            "&:hover": {
                              borderColor: "primary.dark",
                              backgroundColor: "rgba(25, 118, 210, 0.1)",
                            },
                          }}
                        >
                          {loading
                            ? "Getting Location..."
                            : "Get Residential Current Location"}
                        </Button>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Or enter coordinates manually below
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Latitude*"
                        value={formData.residentialLatitude}
                        onChange={handleInputChange("residentialLatitude")}
                        variant="outlined"
                        placeholder="e.g., 28.6217426"
                        helperText="Enter latitude in decimal degrees (between -90 and 90)"
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Longitude*"
                        value={formData.residentialLongitude}
                        onChange={handleInputChange("residentialLongitude")}
                        variant="outlined"
                        placeholder="e.g., 77.0882695"
                        helperText="Enter longitude in decimal degrees (between -180 and 180)"
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          How to find coordinates:
                        </Typography>
                        <Typography variant="body2" component="div">
                          • Open Google Maps → Right-click your residential
                          location → Copy coordinates from the popup
                          <br />
                          • Or click "Get Residential Current Location" button
                          above
                          <br />• Format: Use dot (.) not comma - Correct:
                          28.6217426, Incorrect: 28,6217426
                        </Typography>
                      </Alert>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Correspondence Address */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <FormLabel
                    component="legend"
                    sx={{ fontWeight: "bold", color: "text.primary", mb: 2 }}
                  >
                    Correspondence Address Preference*
                  </FormLabel>
                  <RadioGroup
                    row
                    value={formData.correspondenceAddress}
                    onChange={handleInputChange("correspondenceAddress")}
                    sx={{ gap: 3 }}
                  >
                    <FormControlLabel
                      value="Office"
                      control={<Radio color="primary" />}
                      label="Office Address"
                    />
                    <FormControlLabel
                      value="Residence"
                      control={<Radio color="primary" />}
                      label="Residential Address"
                    />
                  </RadioGroup>
                </CardContent>
              </Card>
            </Grid>

            {/* Photo Upload */}
            <Grid item xs={12}>
              <FileUploadComponent
                fieldName="photoFile"
                label="Upload Recent Photograph"
                orgId={assessorOrgId}
                section="assessor-documents"
                required={true}
                accept=".jpg,.jpeg,.png"
                maxSize={5}
                multiple={false}
                onChange={(fieldName, fileData) => {
                  setFormData((prev) => ({ ...prev, photoFile: fileData }));
                }}
                initialFile={formData.photoFile}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Supported formats: JPG, JPEG, PNG. Maximum file size: 5MB
              </Typography>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                >
                  Back
                </Button>
                <Box sx={{ display: "flex", gap: 2 }}>
                  {/* Show Save button when form is editable AND required fields are filled */}
                  <Button
                    type="submit"
                    variant="outlined"
                    size="large"
                    startIcon={<SaveIcon />}
                  >
                    {isOrgIdExist ? "Update" : "Save "}
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleNext}
                    endIcon={<CheckIcon />}
                  >
                    Next: Qualifications
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );

  // Qualifications Section
  const [qualifications, setQualifications] = useState([]);
  const [qualificationForm, setQualificationForm] = useState({
    degree: "",
    specialization: "",
    college: "",
    country: 101,
    state: "",
    city: "",
    yearOfPassing: "",
    document: null,
  });
  const [editingQualification, setEditingQualification] = useState(null);

  const handleQualificationInputChange = (field) => (event) => {
    setQualificationForm({
      ...qualificationForm,
      [field]: event.target.value,
    });
  };

  const handleQualificationFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setQualificationForm({
        ...qualificationForm,
        document: file,
      });
    }
  };

  const handleAddQualification = async (event) => {
    event.preventDefault();
    if (qualificationForm.degree && qualificationForm.specialization) {
      setLoading(true);
      try {
        const payload = {
          OrgId: assessorOrgId,
          Degree: qualificationForm.degree,
          College: qualificationForm.college,
          Specialization: qualificationForm.specialization,
          YearOfPassing: qualificationForm.yearOfPassing,
          Country: qualificationForm.country,
          State: qualificationForm.state,
          CityName: qualificationForm.city,
          clientip: "0",
          UpdateBy: assessorOrgId,
          Fileupload:
            qualificationForm.document?.fileName || qualificationForm.document,
          New: 1,
        };

        if (editingQualification) {
          payload.id = editingQualification;
          const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/update-qualification`;
          await axios.put(url, payload);
          showSnackbar("Qualification updated successfully!", "success");
        } else {
          const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/insert-qualification`;
          await axios.post(url, payload);
          showSnackbar("Qualification added successfully!", "success");
        }

        // Reload qualifications list
        await loadQualifications(assessorOrgId);
      } catch (error) {
        console.error("Error saving qualification:", error);
        showSnackbar("Error saving qualification", "error");
      } finally {
        setLoading(false);
      }

      setQualificationForm({
        degree: "",
        specialization: "",
        college: "",
        country: 101,
        state: "",
        city: "",
        yearOfPassing: "",
        document: null,
      });
      setEditingQualification(null);
    }
  };

  const handleEditQualification = async (id) => {
    const qualification = qualifications.find((q) => q.id === id);
    if (qualification) {
      // Load states and cities for the selected country and state
      if (qualification.country) {
        await loadStates(qualification.country);
      }
      if (qualification.state) {
        await loadCities(qualification.state);
      }

      setQualificationForm({
        degree: qualification.degree,
        specialization: qualification.specialization,
        college: qualification.college,
        country: qualification.country,
        state: qualification.state,
        city: qualification.city,
        yearOfPassing: qualification.yearOfPassing,
        document: qualification.document,
      });
      setEditingQualification(id);
    }
  };

  const handleDeleteQualification = (id) => {
    setDeleteDialog({
      open: true,
      type: "qualification",
      id: id,
      title: "Delete Qualification",
    });
  };

  const confirmDeleteQualification = async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/delete-qualification/${deleteDialog.id}`;
      await axios.delete(url);
      showSnackbar("Qualification deleted successfully!", "success");

      // Reload qualifications list
      await loadQualifications(assessorOrgId);
    } catch (error) {
      console.error("Error deleting qualification:", error);
      showSnackbar("Error deleting qualification", "error");
    } finally {
      setLoading(false);
      setDeleteDialog({ open: false, type: "", id: null, title: "" });
    }
  };

  const renderQualification = () => (
    <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Avatar sx={{ bgcolor: "secondary.main", mr: 2 }}>
            <SchoolIcon />
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "text.primary" }}
            >
              Educational Qualifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add your educational qualifications starting from the highest
              degree
            </Typography>
          </Box>
        </Box>

        {/* Qualification Form */}
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, color: "text.primary" }}>
              {editingQualification
                ? "Edit Qualification"
                : "Add New Qualification"}
            </Typography>
            <form onSubmit={handleAddQualification}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Degree"
                    value={qualificationForm.degree}
                    onChange={handleQualificationInputChange("degree")}
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Specialization"
                    value={qualificationForm.specialization}
                    onChange={handleQualificationInputChange("specialization")}
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="College / University"
                    value={qualificationForm.college}
                    onChange={handleQualificationInputChange("college")}
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Country*</InputLabel>
                    <Select
                      value={qualificationForm.country}
                      label="Country"
                      onChange={handleQualificationCountryChange}
                    >
                      <MenuItem value="">
                        <em>Select Country</em>
                      </MenuItem>
                      {countries.map((c) => (
                        <MenuItem key={c.id || c.code} value={c.id || c.code}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>State*</InputLabel>
                    <Select
                      value={qualificationForm.state}
                      label="State"
                      onChange={handleQualificationStateChange}
                    >
                      <MenuItem value="">
                        <em>Select State</em>
                      </MenuItem>
                      {states.map((s) => (
                        <MenuItem key={s.id || s.code} value={s.id || s.code}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>City*</InputLabel>
                    <Select
                      value={qualificationForm.city}
                      label="City"
                      onChange={handleQualificationInputChange("city")}
                    >
                      <MenuItem value="">
                        <em>Select City</em>
                      </MenuItem>
                      {cities.map((c) => (
                        <MenuItem key={c.id || c.code} value={c.id || c.name}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Year of Passing"
                    value={qualificationForm.yearOfPassing}
                    onChange={handleQualificationInputChange("yearOfPassing")}
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FileUploadComponent
                    fieldName="qualificationDocument"
                    label="Upload Degree Certificate"
                    orgId={assessorOrgId}
                    section="assessor-qualifications"
                    required={false}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    maxSize={10}
                    multiple={false}
                    onChange={(fieldName, fileData) => {
                      setQualificationForm({
                        ...qualificationForm,
                        document: fileData,
                      });
                    }}
                    initialFile={qualificationForm.document}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={
                        editingQualification ? <SaveIcon /> : <AddIcon />
                      }
                    >
                      {editingQualification
                        ? "Update Qualification"
                        : "Add Qualification"}
                    </Button>
                    {editingQualification && (
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => {
                          setQualificationForm({
                            degree: "",
                            specialization: "",
                            college: "",
                            country: 101,
                            state: "",
                            city: "",
                            yearOfPassing: "",
                            document: null,
                          });
                          setEditingQualification(null);
                        }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        {/* Qualifications Table */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 3, color: "text.primary" }}>
            Added Qualifications ({qualifications.length})
          </Typography>

          {qualifications.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "primary.main" }}>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      #
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Degree
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Specialization
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      College/University
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Country
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      State
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      City
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Year
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Certificate
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qualifications.map((qual, index) => (
                    <TableRow key={qual.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: "medium" }}
                        >
                          {qual.degree}
                        </Typography>
                      </TableCell>
                      <TableCell>{qual.specialization}</TableCell>
                      <TableCell>{qual.college}</TableCell>
                      <TableCell>{qual.countryName || qual.country}</TableCell>
                      <TableCell>{qual.stateName || qual.state}</TableCell>
                      <TableCell>{qual.cityName || qual.city}</TableCell>
                      <TableCell>
                        <Chip
                          label={qual.yearOfPassing}
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {qual.document && qual.document.fileName ? (
                          <Tooltip title={qual.document.fileName}>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => {
                                S3Utils.handleFileViewWithS3(qual.document.fileName, {
                                  showError: (msg) => showSnackbar(msg, "error"),
                                  showLoading: () => setLoading(true),
                                  hideLoading: () => setLoading(false)
                                });
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip
                            title={
                              qual.visible === 0 ? "Editing disabled" : "Edit"
                            }
                          >
                            <span>
                              <IconButton
                                color="primary"
                                onClick={() => handleEditQualification(qual.id)}
                              >
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              qual.visible === 0
                                ? "Deletion disabled"
                                : "Delete"
                            }
                          >
                            <span>
                              <IconButton
                                color="error"
                                onClick={() =>
                                  handleDeleteQualification(qual.id)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Card variant="outlined" sx={{ textAlign: "center", py: 4 }}>
              <SchoolIcon
                sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                No Qualifications Added
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Add your first qualification using the form above.
              </Typography>
            </Card>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button variant="outlined" size="large" onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleNext}
            endIcon={<CheckIcon />}
          >
            Next: Work Experience
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  // Work Experience Section
  const [experiences, setExperiences] = useState([]);
  const [experienceForm, setExperienceForm] = useState({
    employer: "",
    country: 101,
    state: "",
    city: "",
    startDate: "",
    endDate: "",
    currentlyWorking: false,
    position: "",
    responsibilities: "",
    accreditationExperience: "",
    document: null,
    isNablAccredited: "",
    isGovernmentOrganisation: "",
    accreditedDisciplines: [],
  });
  const [editingExperience, setEditingExperience] = useState(null);

  const calculateExperience = (startDate, endDate) => {
    if (!startDate) return { years: 0, months: 0, days: 0 };

    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    return { years, months, days };
  };

  const handleExperienceInputChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setExperienceForm({
      ...experienceForm,
      [field]: value,
    });
  };

  const handleExperienceFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setExperienceForm({
        ...experienceForm,
        document: file,
      });
    }
  };

  const handleAddExperience = async (event) => {
    event.preventDefault();
    if (
      experienceForm.employer &&
      experienceForm.position &&
      experienceForm.startDate
    ) {
      setLoading(true);
      try {
        const { years, months, days } = calculateExperience(
          experienceForm.startDate,
          experienceForm.currentlyWorking ? null : experienceForm.endDate
        );

        // Format Period as "YYYY-MM-DD~YYYY-MM-DD" or "YYYY-MM-DD" for current work
        const period =
          experienceForm.currentlyWorking || !experienceForm.endDate
            ? experienceForm.startDate
            : `${experienceForm.startDate}~${experienceForm.endDate}`;

        const payload = {
          orgId: assessorOrgId,
          employer: experienceForm.employer,
          country: experienceForm.country,
          state: experienceForm.state,
          city: experienceForm.city,
          position: experienceForm.position,
          responsibilities: experienceForm.responsibilities,
          accreditationExperience: experienceForm.accreditationExperience,
          experienceYears: years || 0,
          experienceMonths: months || 0,
          experienceDays: days || 0,
          document:
            experienceForm.document?.fileName || experienceForm.document,
          user: 6,
          isNablAccredited: experienceForm.isNablAccredited || "No",
          relevantExperience: experienceForm.relevantExperience || "0",
          Period: period,
          tilldate: experienceForm.currentlyWorking ? 1 : 0,
        };

        if (editingExperience) {
          payload.id = editingExperience;
          const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/update-work-experience`;
          await axios.put(url, payload);
          showSnackbar("Work experience updated successfully!", "success");
        } else {
          const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/insert-work-experience`;
          await axios.post(url, payload);
          showSnackbar("Work experience added successfully!", "success");
        }

        // Reload work experiences list
        await loadWorkExperiences(assessorOrgId);
      } catch (error) {
        console.error("Error saving work experience:", error);
        showSnackbar("Error saving work experience", "error");
      } finally {
        setLoading(false);
      }

      setExperienceForm({
        employer: "",
        country: 101,
        state: "",
        city: "",
        startDate: "",
        endDate: "",
        currentlyWorking: false,
        position: "",
        responsibilities: "",
        accreditationExperience: "",
        document: null,
        isNablAccredited: "",
        relevantExperience: "",
      });
      setEditingExperience(null);
    }
  };

  const handleEditExperience = async (id) => {
    const experience = experiences.find((exp) => exp.id === id);
    if (experience) {
      // Load states and cities for the selected country and state
      if (experience.country) {
        await loadStates(experience.country);
      }
      if (experience.state) {
        await loadCities(experience.state);
      }

      setExperienceForm({
        employer: experience.employer,
        country: experience.country,
        state: experience.state,
        city: experience.city,
        startDate: experience.startDate,
        endDate: experience.endDate,
        currentlyWorking: experience.currentlyWorking,
        position: experience.position,
        responsibilities: experience.responsibilities,
        accreditationExperience: experience.accreditationExperience,
        document: experience.document,
        isNablAccredited: experience.isNablAccredited || "",
        relevantExperience: experience.relevantExperience || "",
      });
      setEditingExperience(id);
    }
  };

  // Handle radio button changes
  const handleRadioChange = (field) => (event) => {
    setExperienceForm({
      ...experienceForm,
      [field]: event.target.value,
    });
  };

  const handleDeleteExperience = (id) => {
    setDeleteDialog({
      open: true,
      type: "experience",
      id: id,
      title: "Delete Work Experience",
    });
  };

  const confirmDeleteExperience = async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/delete-work-experience/${deleteDialog.id}`;
      await axios.delete(url);
      showSnackbar("Work experience deleted successfully!", "success");

      // Reload work experiences list
      await loadWorkExperiences(assessorOrgId);
    } catch (error) {
      console.error("Error deleting work experience:", error);
      showSnackbar("Error deleting work experience", "error");
    } finally {
      setLoading(false);
      setDeleteDialog({ open: false, type: "", id: null, title: "" });
    }
  };

  const getTotalExperience = () => {
    let totalYears = 0;
    let totalMonths = 0;
    let totalDays = 0;

    experiences.forEach((exp) => {
      totalYears += exp.experienceYears;
      totalMonths += exp.experienceMonths;
      totalDays += exp.experienceDays;
    });

    totalMonths += Math.floor(totalDays / 30);
    totalDays = totalDays % 30;

    totalYears += Math.floor(totalMonths / 12);
    totalMonths = totalMonths % 12;

    return { years: totalYears, months: totalMonths, days: totalDays };
  };

  const renderWorkExperience = () => {
    const totalExp = getTotalExperience();
    return (
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Avatar sx={{ bgcolor: "info.main", mr: 2 }}>
              <WorkIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                Work Experience
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add your professional work experience
              </Typography>
            </Box>
          </Box>

          {/* Total Experience Summary */}
          <Card sx={{ mb: 4, backgroundColor: "primary.main", color: "white" }}>
            <CardContent sx={{ textAlign: "center", py: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Total Professional Experience
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
                {totalExp.years} Years {totalExp.months} Months {totalExp.days}{" "}
                Days
              </Typography>
            </CardContent>
          </Card>

          {/* Experience Form */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, color: "text.primary" }}>
                {editingExperience
                  ? "Edit Work Experience"
                  : "Add New Work Experience"}
              </Typography>
              <form onSubmit={handleAddExperience}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Employer/Organization"
                      value={experienceForm.employer}
                      onChange={handleExperienceInputChange("employer")}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Country*</InputLabel>
                      <Select
                        value={experienceForm.country}
                        label="Country"
                        onChange={handleExperienceCountryChange}
                        required
                      >
                        <MenuItem value="">
                          <em>Select Country</em>
                        </MenuItem>
                        {countries.map((c) => (
                          <MenuItem key={c.id || c.code} value={c.id || c.code}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>State*</InputLabel>
                      <Select
                        value={experienceForm.state}
                        label="State"
                        onChange={handleExperienceStateChange}
                        required
                      >
                        <MenuItem value="">
                          <em>Select State</em>
                        </MenuItem>
                        {states.map((s) => (
                          <MenuItem key={s.id || s.code} value={s.id || s.code}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>City*</InputLabel>
                      <Select
                        value={experienceForm.city}
                        label="City"
                        onChange={handleExperienceInputChange("city")}
                        required
                      >
                        <MenuItem value="">
                          <em>Select City</em>
                        </MenuItem>
                        {cities.map((c) => (
                          <MenuItem key={c.id || c.code} value={c.id || c.name}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={experienceForm.startDate}
                      onChange={handleExperienceInputChange("startDate")}
                      required
                      variant="outlined"
                      inputProps={{
                        max: new Date().toISOString().split("T")[0],
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={experienceForm.endDate}
                      onChange={handleExperienceInputChange("endDate")}
                      variant="outlined"
                      inputProps={{
                        max: new Date().toISOString().split("T")[0],
                        min: experienceForm.startDate,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormLabel
                      component="legend"
                      sx={{ fontWeight: "bold", color: "text.primary", mb: 2 }}
                    >
                      Are you currently working in this organization?*
                    </FormLabel>
                    <RadioGroup
                      row
                      value={experienceForm.currentlyWorking ? "Yes" : "No"}
                      onChange={(e) => {
                        setExperienceForm({
                          ...experienceForm,
                          currentlyWorking: e.target.value === "Yes",
                        });
                      }}
                      sx={{ gap: 2 }}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={<Radio color="primary" />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="No"
                        control={<Radio color="primary" />}
                        label="No"
                      />
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Position Held"
                      value={experienceForm.position}
                      onChange={handleExperienceInputChange("position")}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Major Responsibilities"
                      value={experienceForm.responsibilities}
                      onChange={handleExperienceInputChange("responsibilities")}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormLabel
                      component="legend"
                      sx={{ fontWeight: "bold", color: "text.primary", mb: 2 }}
                    >
                      Experience Related to Accreditation of CAB in this
                      Organization.*
                    </FormLabel>
                    <RadioGroup
                      row
                      value={experienceForm.isNablAccredited || "No"}
                      onChange={handleExperienceInputChange("isNablAccredited")}
                      sx={{ gap: 2 }}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={<Radio color="primary" />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="No"
                        control={<Radio color="primary" />}
                        label="No"
                      />
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Relevant Experience"
                      value={experienceForm.relevantExperience}
                      onChange={handleExperienceInputChange(
                        "relevantExperience"
                      )}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Details of Experience"
                      value={experienceForm.accreditationExperience}
                      onChange={handleExperienceInputChange(
                        "accreditationExperience"
                      )}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FileUploadComponent
                      fieldName="experienceDocument"
                      label="Upload Experience Certificate"
                      orgId={assessorOrgId}
                      section="assessor-experience"
                      required={false}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      maxSize={10}
                      multiple={false}
                      onChange={(fieldName, fileData) => {
                        setExperienceForm({
                          ...experienceForm,
                          document: fileData,
                        });
                      }}
                      initialFile={experienceForm.document}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        startIcon={
                          editingExperience ? <SaveIcon /> : <AddIcon />
                        }
                      >
                        {editingExperience
                          ? "Update Experience"
                          : "Add Experience"}
                      </Button>
                      {editingExperience && (
                        <Button
                          variant="outlined"
                          size="large"
                          onClick={() => {
                            setExperienceForm({
                              employer: "",
                              country: "India",
                              state: "",
                              city: "",
                              startDate: "",
                              endDate: "",
                              currentlyWorking: false,
                              position: "",
                              responsibilities: "",
                              accreditationExperience: "",
                              document: null,
                            });
                            setEditingExperience(null);
                          }}
                        >
                          Cancel Edit
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>

          {/* Experiences Table */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, color: "text.primary" }}>
              Work Experience History ({experiences.length})
            </Typography>

            {experiences.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "primary.main" }}>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Employer
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Position
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Country
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        State
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        City
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Duration
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Experience
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Experience Details
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Document
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {experiences.map((exp) => (
                      <TableRow key={exp.id} hover>
                        <TableCell>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "medium" }}
                          >
                            {exp.employer}
                          </Typography>
                        </TableCell>
                        <TableCell>{exp.position}</TableCell>
                        <TableCell>{exp.countryName || exp.country}</TableCell>
                        <TableCell>{exp.stateName || exp.state}</TableCell>
                        <TableCell>{exp.cityName || exp.city}</TableCell>
                        <TableCell>
                          {new Date(exp.startDate).toLocaleDateString()} ~{" "}
                          {exp.currentlyWorking
                            ? "Present"
                            : new Date(exp.endDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${exp.experienceYears}Y ${exp.experienceMonths}M ${exp.experienceDays}D`}
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200 }}>
                            {exp.accreditationExperience || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {exp.document && exp.document.fileName ? (
                            <Tooltip title={exp.document.fileName}>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => {
                                  S3Utils.handleFileViewWithS3(exp.document.fileName, {
                                    showError: (msg) => showSnackbar(msg, "error"),
                                    showLoading: () => setLoading(true),
                                    hideLoading: () => setLoading(false)
                                  });
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip
                              title={
                                exp.visible === 0 ? "Editing disabled" : "Edit"
                              }
                            >
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEditExperience(exp.id)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                exp.visible === 0
                                  ? "Deletion disabled"
                                  : "Delete"
                              }
                            >
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={() => handleDeleteExperience(exp.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Card variant="outlined" sx={{ textAlign: "center", py: 4 }}>
                <WorkIcon
                  sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  No Work Experience Added
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Add your first work experience using the form above.
                </Typography>
              </Card>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button variant="outlined" size="large" onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleNext}
              endIcon={<CheckIcon />}
            >
              Next: Professional Training
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Professional Training Section
  const [trainings, setTrainings] = useState([]);
  const [trainingForm, setTrainingForm] = useState({
    trainingName: "",
    organizedBy: "",
    country: 101,
    state: "",
    city: "",
    periodFrom: "",
    periodTo: "",
    certificate: null,
  });
  const [editingTraining, setEditingTraining] = useState(null);

  const handleTrainingInputChange = (field) => (event) => {
    setTrainingForm({
      ...trainingForm,
      [field]: event.target.value,
    });
  };

  const handleTrainingFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setTrainingForm({
        ...trainingForm,
        certificate: file,
      });
    }
  };

  const handleAddTraining = async (event) => {
    event.preventDefault();
    if (
      trainingForm.trainingName &&
      trainingForm.organizedBy &&
      trainingForm.periodFrom
    ) {
      setLoading(true);
      try {
        // Format Period: "YYYY-MM-DD" or "YYYY-MM-DD~YYYY-MM-DD"
        const period = trainingForm.periodTo
          ? `${trainingForm.periodFrom}~${trainingForm.periodTo}`
          : trainingForm.periodFrom;

        // Check if tilldate is current date (1 for current, 0 for past)
        const today = new Date().toISOString().split("T")[0];
        const isTillDateCurrent = trainingForm.periodTo === today ? 1 : 0;

        const payload = {
          orgId: assessorOrgId,
          Nametraining: trainingForm.trainingName,
          OrganizedBy: trainingForm.organizedBy,
          Country: trainingForm.country,
          State: trainingForm.state,
          City: trainingForm.city,
          Period: period,
          tilldate: isTillDateCurrent,
          Certificate:
            trainingForm.certificate?.fileName || trainingForm.certificate,
          User: parseInt("6"),
          UpdateBy: assessorOrgId,
        };

        if (editingTraining) {
          payload.id = editingTraining;
          const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/update-training`;
          await axios.put(url, payload);
          showSnackbar("Training updated successfully!", "success");
        } else {
          const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/insert-training`;
          await axios.post(url, payload);
          showSnackbar("Training added successfully!", "success");
        }

        // Reload trainings list
        await loadTrainings(assessorOrgId);
      } catch (error) {
        console.error("Error saving training:", error);
        showSnackbar("Error saving training", "error");
      } finally {
        setLoading(false);
      }

      setTrainingForm({
        trainingName: "",
        organizedBy: "",
        country: 101,
        state: "",
        city: "",
        periodFrom: "",
        periodTo: "",
        certificate: null,
      });
      setEditingTraining(null);
    }
  };

  const handleEditTraining = async (id) => {
    const training = trainings.find((t) => t.id === id);
    if (training) {
      // Load states and cities for the selected country and state
      if (training.country) {
        await loadStates(training.country);
      }
      if (training.state) {
        await loadCities(training.state);
      }

      setTrainingForm({
        trainingName: training.trainingName,
        organizedBy: training.organizedBy,
        country: training.country,
        state: training.state,
        city: training.city,
        periodFrom: training.periodFrom,
        periodTo: training.periodTo,
        certificate: training.certificate,
      });
      setEditingTraining(id);
    }
  };

  const handleDeleteTraining = (id) => {
    setDeleteDialog({
      open: true,
      type: "training",
      id: id,
      title: "Delete Training",
    });
  };

  const confirmDeleteTraining = async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/delete-training/${deleteDialog.id}`;
      await axios.delete(url);
      showSnackbar("Training deleted successfully!", "success");

      // Reload trainings list
      await loadTrainings(assessorOrgId);
    } catch (error) {
      console.error("Error deleting training:", error);
      showSnackbar("Error deleting training", "error");
    } finally {
      setLoading(false);
      setDeleteDialog({ open: false, type: "", id: null, title: "" });
    }
  };

  const getTrainingDuration = (fromDate, toDate) => {
    if (!fromDate || !toDate) return "";

    const from = new Date(fromDate);
    const to = new Date(toDate);
    const diffTime = Math.abs(to - from);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return `${diffDays} days`;
  };

  const renderProfessionalTraining = () => {
    return (
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Avatar sx={{ bgcolor: "warning.main", mr: 2 }}>
              <SchoolIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                Professional Training
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add your professional training and workshops
              </Typography>
            </Box>
          </Box>

          {/* Training Form */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, color: "text.primary" }}>
                {editingTraining ? "Edit Training" : "Add New Training"}
              </Typography>
              <form onSubmit={handleAddTraining}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Training/Workshop Name"
                      value={trainingForm.trainingName}
                      onChange={handleTrainingInputChange("trainingName")}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Organized By"
                      value={trainingForm.organizedBy}
                      onChange={handleTrainingInputChange("organizedBy")}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Country*</InputLabel>
                      <Select
                        value={trainingForm.country}
                        label="Country"
                        onChange={async (e) => {
                          await loadStates(e.target.value);
                          setTrainingForm({
                            ...trainingForm,
                            country: e.target.value,
                            state: "",
                            city: "",
                          });
                          setCities([]);
                        }}
                        required
                      >
                        <MenuItem value="">
                          <em>Select Country</em>
                        </MenuItem>
                        {countries.map((c) => (
                          <MenuItem key={c.id || c.code} value={c.id || c.code}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>State*</InputLabel>
                      <Select
                        value={trainingForm.state}
                        label="State"
                        onChange={async (e) => {
                          await loadCities(e.target.value);
                          setTrainingForm({
                            ...trainingForm,
                            state: e.target.value,
                            city: "",
                          });
                        }}
                        required
                      >
                        <MenuItem value="">
                          <em>Select State</em>
                        </MenuItem>
                        {states.map((s) => (
                          <MenuItem key={s.id || s.code} value={s.id || s.code}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>City*</InputLabel>
                      <Select
                        value={trainingForm.city}
                        label="City"
                        onChange={handleTrainingInputChange("city")}
                        required
                      >
                        <MenuItem value="">
                          <em>Select City</em>
                        </MenuItem>
                        {cities.map((c) => (
                          <MenuItem key={c.id || c.code} value={c.id || c.name}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={trainingForm.periodFrom}
                      onChange={handleTrainingInputChange("periodFrom")}
                      required
                      variant="outlined"
                      inputProps={{
                        max: new Date().toISOString().split("T")[0],
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={trainingForm.periodTo}
                      onChange={handleTrainingInputChange("periodTo")}
                      required
                      variant="outlined"
                      inputProps={{
                        max: new Date().toISOString().split("T")[0],
                        min: trainingForm.periodFrom,
                      }}
                    />
                  </Grid>

                  {trainingForm.periodFrom && trainingForm.periodTo && (
                    <Grid item xs={12}>
                      <Chip
                        label={`Duration: ${getTrainingDuration(
                          trainingForm.periodFrom,
                          trainingForm.periodTo
                        )}`}
                        color="info"
                        variant="outlined"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FileUploadComponent
                      fieldName="trainingCertificate"
                      label="Upload Training Certificate"
                      orgId={assessorOrgId}
                      section="assessor-training"
                      required={false}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      maxSize={10}
                      multiple={false}
                      onChange={(fieldName, fileData) => {
                        setTrainingForm({
                          ...trainingForm,
                          certificate: fileData,
                        });
                      }}
                      initialFile={trainingForm.certificate}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        startIcon={editingTraining ? <SaveIcon /> : <AddIcon />}
                      >
                        {editingTraining ? "Update Training" : "Add Training"}
                      </Button>
                      {editingTraining && (
                        <Button
                          variant="outlined"
                          size="large"
                          onClick={() => {
                            setTrainingForm({
                              trainingName: "",
                              organizedBy: "",
                              country: 101,
                              state: "",
                              city: "",
                              periodFrom: "",
                              periodTo: "",
                              certificate: null,
                            });
                            setEditingTraining(null);
                          }}
                        >
                          Cancel Edit
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>

          {/* Trainings Table */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, color: "text.primary" }}>
              Training History ({trainings.length})
            </Typography>

            {trainings.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "primary.main" }}>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Training
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Organized By
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Country
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        State
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        City
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Period
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Certificate
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trainings.map((training) => (
                      <TableRow key={training.id} hover>
                        <TableCell>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "medium" }}
                          >
                            {training.trainingName}
                          </Typography>
                        </TableCell>
                        <TableCell>{training.organizedBy}</TableCell>
                        <TableCell>
                          {training.countryName || training.country}
                        </TableCell>
                        <TableCell>
                          {training.stateName || training.state}
                        </TableCell>
                        <TableCell>
                          {training.cityName || training.city}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(training.periodFrom).toLocaleDateString()}{" "}
                            - {new Date(training.periodTo).toLocaleDateString()}
                          </Typography>
                          <Chip
                            label={getTrainingDuration(
                              training.periodFrom,
                              training.periodTo
                            )}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        </TableCell>
                        <TableCell>
                          {training.certificate &&
                          training.certificate.fileName ? (
                            <Tooltip title={training.certificate.fileName}>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => {
                                  S3Utils.handleFileViewWithS3(training.certificate.fileName, {
                                    showError: (msg) => showSnackbar(msg, "error"),
                                    showLoading: () => setLoading(true),
                                    hideLoading: () => setLoading(false)
                                  });
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip
                              title={
                                training.visible === "false" ||
                                training.visible === 0
                                  ? "Editing disabled"
                                  : "Edit"
                              }
                            >
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={() =>
                                    handleEditTraining(training.id)
                                  }
                                >
                                  <EditIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                training.visible === "false" ||
                                training.visible === 0
                                  ? "Deletion disabled"
                                  : "Delete"
                              }
                            >
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    handleDeleteTraining(training.id)
                                  }
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Card variant="outlined" sx={{ textAlign: "center", py: 4 }}>
                <SchoolIcon
                  sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  No Training Records
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Add your first training using the form above.
                </Typography>
              </Card>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button variant="outlined" size="large" onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleNext}
              endIcon={<CheckIcon />}
            >
              Next: Field Of Expertise
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Field Of Expertise Section
  const [expertiseFields, setExpertiseFields] = useState([]);
  const [expertiseForm, setExpertiseForm] = useState({
    fieldOfExpertise: "",
    disciplineId: "",
    grpId: "",
    remarks: "",
    document: null,
  });
  const [editingExpertise, setEditingExpertise] = useState(null);
  const [expertisePage, setExpertisePage] = useState(0);
  const [expertiseRowsPerPage, setExpertiseRowsPerPage] = useState(10);
  const [expertiseDisciplines, setExpertiseDisciplines] = useState([]);
  const [loadingExpertiseDisciplines, setLoadingExpertiseDisciplines] = useState(false);
  const [expertiseGroups, setExpertiseGroups] = useState([]);
  const [loadingExpertiseGroups, setLoadingExpertiseGroups] = useState(false);

  const handleExpertiseInputChange = (field) => (event) => {
    setExpertiseForm({
      ...expertiseForm,
      [field]: event.target.value,
    });
  };

  const handleExpertiseFieldChange = async (event) => {
    const fieldType = event.target.value;
    setExpertiseForm({
      ...expertiseForm,
      fieldOfExpertise: fieldType,
      disciplineId: "",
      grpId: "",
    });

    if (fieldType) {
      await loadExpertiseDisciplines(fieldType);
    } else {
      setExpertiseDisciplines([]);
      setExpertiseGroups([]);
    }
  };

  const loadExpertiseDisciplines = async (fieldType) => {
    if (!fieldType) {
      setExpertiseDisciplines([]);
      return;
    }

    setLoadingExpertiseDisciplines(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/disciplines/${fieldType}`;
      const response = await axios.get(url);
      
      if (response.data.success && response.data.data) {
        setExpertiseDisciplines(response.data.data);
      } else {
        setExpertiseDisciplines([]);
        showSnackbar("Failed to load disciplines", "error");
      }
    } catch (error) {
      console.error("Error loading expertise disciplines:", error);
      setExpertiseDisciplines([]);
      showSnackbar("Error loading disciplines", "error");
    } finally {
      setLoadingExpertiseDisciplines(false);
    }
  };

  const loadExpertiseGroups = async (labTypeId, disciplineId) => {
    if (!labTypeId || !disciplineId) {
      setExpertiseGroups([]);
      return;
    }

    setLoadingExpertiseGroups(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/groups/${labTypeId}/${disciplineId}`;
      const response = await axios.get(url);
      
      if (response.data.success && response.data.data) {
        setExpertiseGroups(response.data.data);
      } else {
        setExpertiseGroups([]);
        showSnackbar("Failed to load groups", "error");
      }
    } catch (error) {
      console.error("Error loading expertise groups:", error);
      setExpertiseGroups([]);
      showSnackbar("Error loading groups", "error");
    } finally {
      setLoadingExpertiseGroups(false);
    }
  };

  const handleExpertiseDisciplineChange = async (event) => {
    const disciplineId = event.target.value;
    setExpertiseForm({
      ...expertiseForm,
      disciplineId: disciplineId,
      grpId: "",
    });

    if (disciplineId && expertiseForm.fieldOfExpertise) {
      await loadExpertiseGroups(expertiseForm.fieldOfExpertise, disciplineId);
    } else {
      setExpertiseGroups([]);
    }
  };

  const loadExpertiseFields = async (orgId) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/get-expertise-fields/${orgId}`;
      const response = await axios.get(url);

      if (response.data.success && Array.isArray(response.data.data)) {
        const formattedData = response.data.data.map((exp) => ({
          id: exp.id,
          fieldOfExpertise: exp.ExpertiseField,
          disciplineId: exp.DisciplineId,
          disciplineName: exp.disciplineName,
          grpId: exp.ExpertiseGroup,
          groupName: exp.groupName,
          remarks: exp.Remarks || "",
          document: createFileObject(exp.Document),
        }));

        setExpertiseFields(formattedData);
      }
    } catch (error) {
      console.error("Error loading expertise fields:", error);
    }
  };

  const handleExpertiseSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      if (!expertiseForm.fieldOfExpertise || !expertiseForm.disciplineId) {
        showSnackbar("Field of Expertise and Discipline are required", "error");
        setLoading(false);
        return;
      }

      const payload = {
        OrgId: assessorOrgId,
        ExpertiseField: expertiseForm.fieldOfExpertise,
        DisciplineId: expertiseForm.disciplineId,
        Remarks: expertiseForm.remarks,
        Document: expertiseForm.document?.fileName || "",
        ExpertiseGroup: expertiseForm.grpId,
        ClientIP: "0",
        UpdateBy: assessorOrgId,
      };

      if (editingExpertise) {
        const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/update-expertise-field/${editingExpertise}`;
        const response = await axios.put(url, payload);

        if (response.data.success) {
          showSnackbar("Expertise field updated successfully", "success");
          await loadExpertiseFields(assessorOrgId);
          setEditingExpertise(null);
        } else {
          showSnackbar("Failed to update expertise field", "error");
        }
      } else {
        const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/insert-expertise-field`;
        const response = await axios.post(url, payload);

        if (response.data.success) {
          showSnackbar("Expertise field added successfully", "success");
          await loadExpertiseFields(assessorOrgId);
        } else {
          showSnackbar("Failed to add expertise field", "error");
        }
      }

      setExpertiseForm({
        fieldOfExpertise: "",
        disciplineId: "",
        grpId: "",
        remarks: "",
        document: null,
      });
      setExpertiseDisciplines([]);
      setExpertiseGroups([]);
    } catch (error) {
      console.error("Error saving expertise field:", error);
      showSnackbar("Error saving expertise field", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditExpertise = (id) => {
    const expertise = expertiseFields.find((exp) => exp.id === id);
    if (expertise) {
      setExpertiseForm(expertise);
      setEditingExpertise(id);
      if (expertise.fieldOfExpertise) {
        loadExpertiseDisciplines(expertise.fieldOfExpertise);
      }
    }
  };

  const handleDeleteExpertise = async (id) => {
    setDeleteDialog({
      open: true,
      type: "expertise",
      id: id,
      title: "Delete Field of Expertise",
    });
  };

  const confirmDeleteExpertise = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/delete-expertise-field/${deleteDialog.id}`;
      const response = await axios.delete(url);

      if (response.data.success) {
        showSnackbar("Expertise field deleted successfully", "success");
        await loadExpertiseFields(assessorOrgId);
      } else {
        showSnackbar("Failed to delete expertise field", "error");
      }
    } catch (error) {
      console.error("Error deleting expertise field:", error);
      showSnackbar("Error deleting expertise field", "error");
    } finally {
      setDeleteDialog({ open: false, type: "", id: null, title: "" });
    }
  };

  const renderFieldOfExpertise = () => {
    return (
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Avatar sx={{ bgcolor: "success.main", mr: 2 }}>
              <WorkIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                Field Of Expertise
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add your field of expertise and disciplines
              </Typography>
            </Box>
          </Box>

          <form onSubmit={handleExpertiseSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel>Field of Expertise</InputLabel>
                  <Select
                    value={expertiseForm.fieldOfExpertise}
                    label="Field of Expertise"
                    onChange={handleExpertiseFieldChange}
                  >
                    <MenuItem value="">
                      <em>Select Field</em>
                    </MenuItem>
                    <MenuItem value="1">Testing</MenuItem>
                    <MenuItem value="2">Calibration</MenuItem>
                    <MenuItem value="3">Medical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel>Discipline</InputLabel>
                  <Select
                    value={expertiseForm.disciplineId}
                    label="Discipline"
                    onChange={handleExpertiseDisciplineChange}
                    disabled={loadingExpertiseDisciplines || expertiseDisciplines.length === 0}
                  >
                    <MenuItem value="">
                      <em>Select Discipline</em>
                    </MenuItem>
                    {loadingExpertiseDisciplines ? (
                      <MenuItem disabled>
                        <em>Loading disciplines...</em>
                      </MenuItem>
                    ) : expertiseDisciplines.length === 0 ? (
                      <MenuItem disabled>
                        <em>Select Field of Expertise first</em>
                      </MenuItem>
                    ) : (
                      expertiseDisciplines.map((discipline) => (
                        <MenuItem key={discipline.id} value={discipline.id}>
                          {discipline.disciplineName}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {expertiseForm.fieldOfExpertise !== "3" && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" required>
                    <InputLabel>Group</InputLabel>
                    <Select
                      value={expertiseForm.grpId}
                      label="Group"
                      onChange={handleExpertiseInputChange("grpId")}
                      disabled={loadingExpertiseGroups || expertiseGroups.length === 0}
                    >
                      <MenuItem value="">
                        <em>Select Group</em>
                      </MenuItem>
                      {loadingExpertiseGroups ? (
                        <MenuItem disabled>
                          <em>Loading groups...</em>
                        </MenuItem>
                      ) : expertiseGroups.length === 0 ? (
                        <MenuItem disabled>
                          <em>Select Discipline first</em>
                        </MenuItem>
                      ) : (
                        expertiseGroups.map((group) => (
                          <MenuItem key={group.grpId} value={group.grpId}>
                            {group.groupName}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks"
                  multiline
                  rows={3}
                  value={expertiseForm.remarks}
                  onChange={handleExpertiseInputChange("remarks")}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <FileUploadComponent
                  key={editingExpertise || 'new-expertise'}
                  fieldName="expertiseDocument"
                  label="Upload Document"
                  orgId={assessorOrgId}
                  section="expertise-documents"
                  required={false}
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={10}
                  multiple={false}
                  onChange={(fieldName, fileData) => {
                    setExpertiseForm((prev) => ({ ...prev, document: fileData }));
                  }}
                  initialFile={expertiseForm.document}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={editingExpertise ? <SaveIcon /> : <AddIcon />}
                  >
                    {editingExpertise ? "Update Expertise" : "Add Expertise"}
                  </Button>
                  {editingExpertise && (
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => {
                        setExpertiseForm({
                          fieldOfExpertise: "",
                          disciplineId: "",
                          remarks: "",
                          document: null,
                        });
                        setEditingExpertise(null);
                        setExpertiseDisciplines([]);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>

          {/* Expertise Fields Table */}
          {expertiseFields.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 4 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "primary.light" }}>
                    <TableCell sx={{ fontWeight: "bold",color:"white" }}>Field of Expertise</TableCell>
                    <TableCell sx={{ fontWeight: "bold",color:"white" }}>Discipline</TableCell>
                    <TableCell sx={{ fontWeight: "bold",color:"white" }}>Remarks</TableCell>
                    <TableCell sx={{ fontWeight: "bold",color:"white" }}>Document</TableCell>
                    <TableCell sx={{ fontWeight: "bold", textAlign: "center",color:"white" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expertiseFields
                    .slice(expertisePage * expertiseRowsPerPage, expertisePage * expertiseRowsPerPage + expertiseRowsPerPage)
                    .map((exp) => (
                    <TableRow key={exp.id}>
                      <TableCell>{fieldOfExpertiseLabels[exp.fieldOfExpertise] || exp.fieldOfExpertise}</TableCell>
                      <TableCell>
                        {exp.disciplineName}
                      </TableCell>
                      <TableCell>{exp.remarks || "-"}</TableCell>
                      <TableCell>
                        {exp.document && exp.document.fileName ? (
                          <Tooltip title={exp.document.fileName}>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => {
                                S3Utils.handleFileViewWithS3(exp.document.fileName, {
                                  showError: (msg) => showSnackbar(msg, "error"),
                                  showLoading: () => setLoading(true),
                                  hideLoading: () => setLoading(false)
                                });
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                          <Tooltip title="Edit">
                            <span>
                              <IconButton
                                color="primary"
                                onClick={() => handleEditExpertise(exp.id)}
                              >
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <span>
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteExpertise(exp.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={expertiseFields.length}
                rowsPerPage={expertiseRowsPerPage}
                page={expertisePage}
                onPageChange={(event, newPage) => setExpertisePage(newPage)}
                onRowsPerPageChange={(event) => {
                  setExpertiseRowsPerPage(parseInt(event.target.value, 10));
                  setExpertisePage(0);
                }}
              />
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 4 }}>
              No expertise fields added yet. Please add your field of expertise above.
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button variant="outlined" size="large" onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleNext}
              endIcon={<CheckIcon />}
            >
              Next: References
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // References Section
  const [referees, setReferees] = useState([]);
  const [refereeForm, setRefereeForm] = useState({
    referenceType: "Head of Organization",
    name: "",
    designation: "",
    organization: "",
    country: 101,
    state: "",
    city: "",
    mobile: "",
    email: "",
  });
  const [editingReferee, setEditingReferee] = useState(null);

  const handleRefereeInputChange = (field) => (event) => {
    setRefereeForm({
      ...refereeForm,
      [field]: event.target.value,
    });
  };

  const handleAddReferee = async (event) => {
    event.preventDefault();

    // Only allow single entry if not editing
    if (referees.length > 0 && !editingReferee) {
      showSnackbar("Only one reference can be added", "warning");
      return;
    }

    if (
      refereeForm.name &&
      refereeForm.designation &&
      refereeForm.organization &&
      refereeForm.mobile
    ) {
      setLoading(true);
      try {
        const payload = {
          orgId: assessorOrgId,
          Authority: refereeForm.referenceType,
          Name: refereeForm.name,
          Designation: refereeForm.designation,
          Organization: refereeForm.organization,
          Country: refereeForm.country,
          State: refereeForm.state,
          CityName: refereeForm.city,
          Mobile: refereeForm.mobile,
          Email: refereeForm.email || null,
          User: parseInt("6"),
          Insert_By: assessorOrgId,
        };

        // Note: Backend only has insert endpoint for referee, no update
        const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/insert-referee`;
        await axios.post(url, payload);
        showSnackbar(
          editingReferee
            ? "Reference updated successfully!"
            : "Reference added successfully!",
          "success"
        );

        // Reload referees list
        await loadReferees(assessorOrgId);
      } catch (error) {
        console.error("Error saving referee:", error);
        showSnackbar("Error saving reference", "error");
      } finally {
        setLoading(false);
      }

      setRefereeForm({
        referenceType: "Head of Organization",
        name: "",
        designation: "",
        organization: "",
        country: 101,
        state: "",
        city: "",
        mobile: "",
        email: "",
      });
      setEditingReferee(null);
    }
  };

  const handleEditReferee = async (id) => {
    const referee = referees.find((r) => r.id === id);
    if (referee) {
      // Load states and cities for the selected country and state
      if (referee.country) {
        await loadStates(referee.country);
      }
      if (referee.state) {
        await loadCities(referee.state);
      }

      setRefereeForm({
        referenceType: referee.referenceType,
        name: referee.name,
        designation: referee.designation,
        organization: referee.organization,
        country: referee.country,
        state: referee.state,
        city: referee.city,
        mobile: referee.mobile,
        email: referee.email,
      });
      setEditingReferee(id);
    }
  };

  const renderReferee = () => {
    return (
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Avatar sx={{ bgcolor: "success.main", mr: 2 }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                References
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add references who can verify your professional information
              </Typography>
            </Box>
          </Box>

          {/* Reference Form - Show when no reference exists or when editing */}
          {(referees.length === 0 || editingReferee) && (
            <Card variant="outlined" sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, color: "text.primary" }}>
                  {editingReferee ? "Edit Reference" : "Add Reference"}
                </Typography>
                <form onSubmit={handleAddReferee}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormLabel
                        component="legend"
                        sx={{
                          fontWeight: "bold",
                          color: "text.primary",
                          mb: 2,
                        }}
                      >
                        Reference Type*
                      </FormLabel>
                      <RadioGroup
                        row
                        value={refereeForm.referenceType}
                        onChange={handleRefereeInputChange("referenceType")}
                        sx={{ gap: 3 }}
                      >
                        <FormControlLabel
                          value="Head of Organization"
                          control={<Radio color="primary" />}
                          label="Head of Organization"
                        />
                        <FormControlLabel
                          value="Immediate Superior Officer"
                          control={<Radio color="primary" />}
                          label="Immediate Superior Officer"
                        />
                        <FormControlLabel
                          value="NABL Lead Assessor"
                          control={<Radio color="primary" />}
                          label="NABL Lead Assessor"
                        />
                      </RadioGroup>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={refereeForm.name}
                        onChange={handleRefereeInputChange("name")}
                        required
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Designation"
                        value={refereeForm.designation}
                        onChange={handleRefereeInputChange("designation")}
                        required
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Organization"
                        value={refereeForm.organization}
                        onChange={handleRefereeInputChange("organization")}
                        required
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Country*</InputLabel>
                        <Select
                          value={refereeForm.country}
                          label="Country"
                          onChange={handleRefereeCountryChange}
                          required
                        >
                          <MenuItem value="">
                            <em>Select Country</em>
                          </MenuItem>
                          {countries.map((c) => (
                            <MenuItem
                              key={c.id || c.code}
                              value={c.id || c.code}
                            >
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>State*</InputLabel>
                        <Select
                          value={refereeForm.state}
                          label="State"
                          onChange={handleRefereeStateChange}
                          required
                        >
                          <MenuItem value="">
                            <em>Select State</em>
                          </MenuItem>
                          {states.map((s) => (
                            <MenuItem
                              key={s.id || s.code}
                              value={s.id || s.code}
                            >
                              {s.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>City*</InputLabel>
                        <Select
                          value={refereeForm.city}
                          label="City"
                          onChange={handleRefereeInputChange("city")}
                          required
                        >
                          <MenuItem value="">
                            <em>Select City</em>
                          </MenuItem>
                          {cities.map((c) => (
                            <MenuItem
                              key={c.id || c.code}
                              value={c.id || c.name}
                            >
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Mobile Number"
                        value={refereeForm.mobile}
                        onChange={handleRefereeInputChange("mobile")}
                        required
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={refereeForm.email}
                        onChange={handleRefereeInputChange("email")}
                        required
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={
                            editingReferee ? <SaveIcon /> : <AddIcon />
                          }
                        >
                          {editingReferee
                            ? "Update Reference"
                            : "Add Reference"}
                        </Button>
                        {editingReferee && (
                          <Button
                            variant="outlined"
                            size="large"
                            onClick={() => {
                              setRefereeForm({
                                referenceType: "Head of Organization",
                                name: "",
                                designation: "",
                                organization: "",
                                country: 101,
                                state: "",
                                city: "",
                                mobile: "",
                                email: "",
                              });
                              setEditingReferee(null);
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Display existing referee if present and not editing */}
          {referees.length > 0 && !editingReferee && (
            <Card variant="outlined" sx={{ mb: 4 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ color: "text.primary" }}>
                    Reference Details
                  </Typography>
                  <IconButton
                    color="primary"
                    onClick={() => handleEditReferee(referees[0].id)}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Reference Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {referees[0].referenceType}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {referees[0].name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Designation
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {referees[0].designation}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Organization
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {referees[0].organization}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Mobile
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {referees[0].mobile}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {referees[0].email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {referees[0].cityName || referees[0].city},{" "}
                      {referees[0].stateName || referees[0].state}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Important Note */}
          <Card
            variant="outlined"
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: "#fff8e1",
              borderColor: "#ffd54f",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
              📝 Important: The reference should be someone who can verify the
              accuracy of the information provided in this biodata. This could
              be your Head of Organization, Immediate Superior Officer, or a
              NABL Lead Assessor.
            </Typography>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button variant="outlined" size="large" onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleNext}
              endIcon={<CheckIcon />}
            >
              Next: Agreement
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Agreement Section
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementDate, setAgreementDate] = useState("");
  const [witnesses, setWitnesses] = useState([
    {
      id: 1,
      name: "",
      address: "",
      mobile: "",
      email: "",
    },
    {
      id: 2,
      name: "",
      address: "",
      mobile: "",
      email: "",
    },
  ]);

  const handleWitnessChange = (id, field) => (event) => {
    const updatedWitnesses = witnesses.map((witness) =>
      witness.id === id ? { ...witness, [field]: event.target.value } : witness
    );
    setWitnesses(updatedWitnesses);
  };

  const handleAgreementAcceptance = async (event) => {
    const isChecked = event.target.checked;
    setAgreementAccepted(isChecked);
  };

  const handleSubmitApplication = async () => {
    if (agreementAccepted) {
      setLoading(true);
      try {
        const payload = {
          Orgid: assessorOrgId,
          WitnessName1: witnesses[0]?.name || "",
          WitnessAddress1: witnesses[0]?.address || "",
          WitnessMobile1: witnesses[0]?.mobile || "",
          WitnessEmail1: witnesses[0]?.email || "",
          WitnessName2: witnesses[1]?.name || "",
          WitnessAddress2: witnesses[1]?.address || "",
          WitnessMobile2: witnesses[1]?.mobile || "",
          WitnessEmail2: witnesses[1]?.email || "",
          Insert_By: 6,
          ClientIP: "",
        };

        const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/insert-agreement`;
        const response = await axios.post(url, payload);

        if (response.data && response.data.success) {
          showSnackbar("Submitted successfully!", "success");
          // Navigate or perform other actions as needed
        } else {
          showSnackbar("Error submitting", "error");
        }
      } catch (error) {
        console.error("Error submitting:", error);
        showSnackbar("Error submitting", "error");
      } finally {
        setLoading(false);
      }
    } else {
      showSnackbar("Please accept the agreement to submit", "error");
    }
  };

  const renderAgreement = () => {
    return (
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Avatar sx={{ bgcolor: "error.main", mr: 2 }}>
              <DescriptionIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                Agreement
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review and accept the terms to complete your application
              </Typography>
            </Box>
          </Box>

          {/* Agreement Date */}
          <Card sx={{ mb: 3, backgroundColor: "primary.main", color: "white" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Agreement made on:{" "}
                {agreementDate
                  ? new Date(agreementDate).toLocaleString("en-US", {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                      second: "numeric",
                      hour12: true,
                    })
                  : "-"}
              </Typography>
              <Typography variant="body2">
                Between {formData.name || "Assessor"} and National Accreditation
                Board for Testing and Calibration Laboratories, a constituent
                board of Quality Council of India (NABL-QCI)
              </Typography>
            </CardContent>
          </Card>

          {/* Agreement Content */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: "primary.main" }}>
                Terms and Conditions
              </Typography>
              <Box sx={{ maxHeight: "400px", overflow: "auto", p: 1 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  This agreement is made between {formData.name || "-"} and the
                  National Accreditation Board for Testing and Calibration
                  Laboratories, a constituent board of Quality Council of India
                  (NABL-QCI), the accreditation body, for empanelment of the
                  former as Lead/ Technical Assessor with NABL for carrying out
                  assessment of Conformity Assessment Bodies (Testing including
                  Medical laboratories, Calibration laboratories, Reference
                  Material Producer - RMP and Proficiency Testing Provider- PTP)
                  seeking accreditation from NABL on the following terms of
                  reference:
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
                >
                  A. Assignment
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  NABL shall approach the Lead / Technical Assessor for the
                  assessment of CABs as and when required, on a case-to-case
                  basis. NABL will give sufficient notice to the Assessor in
                  this regard to plan his / her visit. NABL will engage an
                  assessor on the need basis. It is the prerogative of NABL to
                  engage an assessor. In no way, by this contract or other
                  means, assessor has right to demand for an assignment.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  Appointment of assessor as Lead / Technical Assessor for a
                  particular assessment is the sole discretion of NABL,
                  depending upon the requirement and expertise of an assessor.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  NABL shall provide all relevant documents to the Lead /
                  Technical Assessor needed for the assessment of an applicant /
                  accredited CAB.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall keep themselves
                  well-versed and update with NABL criteria for Accreditation,
                  Standards & Test methods, Rules and Procedures for relevant
                  discipline contained in various documents, Supplements and
                  other communications as provided by NABL.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall normally make themselves
                  available for the assessment visit when approached by NABL. If
                  not so, due to valid reasons, he/ she should inform the same
                  to NABL immediately so that an alternate arrangement may be
                  made by NABL.
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
                >
                  B. Administrative and Financial
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall abide strictly by all
                  prevailing administrative and financial rules and procedures
                  of NABL / QCI. In case of any deviation, it has to be approved
                  by NABL in advance. Any violation of NABL Administrative and
                  Financial Rules by an Assessor will attract action such as the
                  debarring of an assessor or making them inactive from NABL
                  empanelment.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall inform their current
                  employers and take formal permission to work for NABL on
                  assignment basis wherever required. NABL, in no way, will be
                  the party between the assessor and their employer regarding
                  NABL assignments.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall keep NABL informed about
                  their up-to-date particulars regarding employment,
                  qualification, training, contact address and any other
                  relevant information.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  NABL shall not be responsible for any physical or material
                  loss to the assessor during the assessment or during transit
                  on an assignment.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  NABL shall pay honorarium to the Lead / Technical Assessor for
                  the assessment of the CAB and shall take care of their lodging
                  and boarding directly or through CAB being assessed as per
                  NABL / QCI rules and regulation. Lead / Technical Assessor
                  shall be present throughout the entire assessment period
                  starting from the opening meeting till the closing meeting.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor declare to route all
                  correspondences with the CAB relating to the assessment
                  through NABL secretariat or to communicate the same by sending
                  copies of those correspondences to NABL.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall make themselves available
                  for any training / refresher courses / conclave etc. organized
                  by NABL in order to upgrade and update the knowledge.
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
                >
                  C. Confidentiality
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall maintain the
                  confidentially of the information pertaining to the applicant
                  / accredited CABs gathered during the assessment or through
                  other means.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor may disclose information, which
                  is confided to themselves as secret or of which the
                  confidential nature is obvious, only to those who have been
                  meant for this purpose, through a specific rule or explicit
                  consent from NABL. The Lead / Technical Assessor declare to
                  maintain this secrecy even after the termination / expiry of
                  this contract.
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
                >
                  D. Conflict of Interest
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor also pledge to act as an
                  Assessor of CABs only for NABL. In case, if NABL Assessor gets
                  a request from other Accreditation Body to provide his / her
                  service for the area (ISO/IEC 17025, ISO 15189, ISO/IEC 17043,
                  ISO Guide 34/ ISO 17034) in any form (such as assessor,
                  advisor, committee member etc.), he / she shall inform NABL
                  and take a prior approval before accepting such assignment.
                  Any violation found in this regard may attract action such as
                  debarring of an assessor from NABL list.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall ensure that no personal
                  bias and influence or any external pressure, commercial or
                  otherwise, is allowed to compromise impartiality while
                  carrying out assessment of applicant / accredited laboratory
                  and making recommendations to NABL.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  To avoid conflict of interest, the Lead / Technical Assessor
                  shall inform NABL in advance about his / her commercial
                  interests including competition, providing guidance / training
                  / internal audit / capacity building / laboratory development
                  or up-gradation etc., relationship, ex employer or from the
                  relationships of its personnel etc., if any, in the applicant
                  / accredited CAB for which he is approached for assessment /
                  any other assignment. To avoid conflict of interest, Lead /
                  Technical Assessor shall also inform NABL, in case of prior
                  ill-will between the Assessor and one or more of the CAB staff
                  because of acrimonious disagreement in technical / scientific
                  society activities. NABL shall use its discretion in such a
                  case regarding his replacement or otherwise, which shall be
                  communicated to the Assessor in writing.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor, in case he / she is requested
                  by NABL to act as an Assessor of a CAB where he / she has
                  worked during the past 5 years could endanger the independence
                  / impartiality of NABL, shall declare the same without being
                  asked for, to inform NABL about this at the time when he / she
                  is requested to act as Assessor by NABL.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall not make any statement or
                  act in such a manner, as to bring NABL / QCI into disrepute.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall inform NABL on problems of
                  general nature, which can affect the image and the working
                  method of NABL.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall not make any assurance to
                  the applicant / accredited CAB regarding accreditation on
                  behalf of NABL or on his own.
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
                >
                  E. Terms, renewal and Termination of Contract
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  If the Lead / Technical Assessor want to discontinue his / her
                  empanelment for any reasons, he/ she shall inform NABL in
                  writing.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall discharge his / her duties
                  during the assessment in such a manner so as to contribute
                  positively to the work of the assessment team and extend full
                  cooperation to the team leader responsible for overall
                  coordination.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The Lead / Technical Assessor shall declare to be available
                  for NABL at least for 2 assessment visits per years, if asked
                  by NABL.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  NABL shall be free to delete the names of an empanelled
                  Assessor without any correspondence, in the following or other
                  cases:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ lineHeight: 1.6, mb: 2, ml: 2 }}
                >
                  • If he/she fails to report for pre-scheduled on-site
                  assessment without sufficient reasons to the satisfaction of
                  NABL.
                  <br />
                  • Due to his/her continued inability to accept on-site
                  assessment assignments over a period of time without
                  sufficient reasoning to the satisfaction of NABL.
                  <br />
                  • Due to indifferent attitude, improper conduct, disregard and
                  non-compliance to the NABL rules and procedures.
                  <br />
                  • In case, Lead / Technical Assessor is found to be involved
                  in any kind of malpractices and unethical practices like undue
                  demand in any form from the CAB, breach of confidentiality,
                  integrity etc.
                  <br />• In case, Lead / Technical Assessor performance / act
                  are not found satisfactory by NABL.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  The empanelment of Lead / Technical Assessor shall be valid
                  for an initial period of 5 years which may later be extended
                  subject to review of performance by NABL.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  Any deviation to a clause of this contract is only possible
                  through a written approval from NABL.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  In case any dispute arises about the interpretation of the
                  various clauses of the agreement, the decision of NABL shall
                  be final and binding on both the parties.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                  All disputes, if any, arising out of this contract, that
                  remain unresolved through mechanism provided by NABL are
                  subject to the exclusive jurisdiction of the Courts at New
                  Delhi and none other.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Witness Section */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 3, display: "flex", alignItems: "center" }}
              >
                <AssignmentIcon sx={{ mr: 1 }} />
                Witness Details
              </Typography>

              <Grid container spacing={3}>
                {witnesses.map((witness, index) => (
                  <Grid item xs={12} md={6} key={witness.id}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: "bold" }}
                      >
                        Witness {index + 1}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Name"
                            value={witness.name}
                            onChange={handleWitnessChange(witness.id, "name")}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address"
                            value={witness.address}
                            onChange={handleWitnessChange(
                              witness.id,
                              "address"
                            )}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Mobile"
                            value={witness.mobile}
                            onChange={handleWitnessChange(witness.id, "mobile")}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={witness.email}
                            onChange={handleWitnessChange(witness.id, "email")}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Agreement Acceptance */}
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              backgroundColor: agreementAccepted ? "#f0f9f0" : "#fffaf0",
            }}
          >
            <CardContent>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreementAccepted}
                    onChange={handleAgreementAcceptance}
                    icon={<DescriptionIcon />}
                    checkedIcon={<CheckIcon sx={{ color: "success.main" }} />}
                    size="large"
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    I have read and understood all the terms and conditions
                    mentioned in this agreement and I accept to abide by them.
                  </Typography>
                }
              />
              {agreementAccepted && (
                <Chip
                  label="Agreement Accepted"
                  color="success"
                  variant="filled"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Button variant="outlined" size="large" onClick={handleBack}>
              Back
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmitApplication}
                sx={{
                  backgroundColor: agreementAccepted
                    ? "success.main"
                    : "grey.400",
                  "&:hover": {
                    backgroundColor: agreementAccepted
                      ? "success.dark"
                      : "grey.400",
                  },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Enhanced Navigation Component
  const NavigationTabs = () => (
    <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            mt: 2,
            flexWrap: "wrap",
          }}
        >
          {steps.map((step, index) => (
            <Chip
              key={step}
              label={step}
              onClick={() => {
                setActiveStep(index);
                setActiveTab(
                  [
                    "basicdetail",
                    "qualification",
                    "workexperience",
                    "professionaltraining",
                    "fieldofexpertise",
                    "referee",
                    "agreement",
                  ][index]
                );
              }}
              color={activeStep === index ? "primary" : "default"}
              variant={activeStep === index ? "filled" : "outlined"}
              sx={{ mb: 1 }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  // Breadcrumb Navigation
  const BreadcrumbNavigation = () => (
    <Box sx={{ mb: 2 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
      >
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => {
            setActiveStep(0);
            setActiveTab("basicdetail");
          }}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Typography color="text.primary">{steps[activeStep]}</Typography>
      </Breadcrumbs>
    </Box>
  );

  // Tab renderer: return the appropriate section based on activeTab
  const renderTabContent = (tab) => {
    switch (tab) {
      case "basicdetail":
        return renderBasicDetails();
      case "qualification":
        return renderQualification();
      case "workexperience":
        return renderWorkExperience();
      case "professionaltraining":
        return renderProfessionalTraining();
      case "fieldofexpertise":
        return renderFieldOfExpertise();
      case "referee":
        return renderReferee();
      case "agreement":
        return renderAgreement();
      default:
        return null;
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Avatar sx={{ bgcolor: "white", mr: 2 }}>
            <DescriptionIcon color="primary" />
          </Avatar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Assessor Bio-Data
          </Typography>
          <Chip
            label={`Step ${activeStep + 1} of ${steps.length}`}
            color="primary"
            variant="outlined"
            sx={{ color: "white", borderColor: "white" }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Breadcrumb Navigation */}
        <BreadcrumbNavigation />

        {/* Progress Stepper */}
        <NavigationTabs />

        {/* Main Content (tab renderer) */}
        {renderTabContent(activeTab)}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() =>
            setDeleteDialog({ open: false, type: "", id: null, title: "" })
          }
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{deleteDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this {deleteDialog.type}? This
              action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setDeleteDialog({ open: false, type: "", id: null, title: "" })
              }
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (deleteDialog.type === "qualification") {
                  confirmDeleteQualification();
                } else if (deleteDialog.type === "experience") {
                  confirmDeleteExperience();
                } else if (deleteDialog.type === "training") {
                  confirmDeleteTraining();
                } else if (deleteDialog.type === "expertise") {
                  confirmDeleteExpertise();
                }
              }}
              color="error"
              variant="contained"
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AssessorEditById;
